import { Fragment, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Line,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { simulaSpesa } from '@mastro/core/previsionale';
import { api, type Categoria, type DettaglioPrevisionale, type Previsione, type Scenario, type VoceBudget } from '../api';
import { GestioneScenari, SceltaScenario, riassunto } from './scenari';
import {
  Caricamento,
  Campo,
  Dialogo,
  Errore,
  Etichetta,
  Importo,
  Scheda,
  Vuoto,
  useAvvisi,
  useCarico,
} from '../componenti/base';
import { euroCompatto, formattaData, formattaEuro, plurale } from '../formato';
import { useSessione } from '../main';

/**
 * Lo stesso orizzonte in due unità: a settimane non è «mesi × 4» buttato nel
 * parametro dei mesi, che sforerebbe il limite dell'API e farebbe fallire la
 * chiamata invece di mostrare meno periodi.
 */
const ORIZZONTI = [
  { mesi: 3, settimane: 13, testo: '3 mesi' },
  { mesi: 6, settimane: 26, testo: '6 mesi' },
  { mesi: 12, settimane: 52, testo: '12 mesi' },
];

export function Previsionale() {
  const { organizzazione } = useSessione();
  const [granularita, setGranularita] = useState<'MESE' | 'SETTIMANA'>('MESE');
  const [orizzonte, setOrizzonte] = useState(12);

  const scelto = ORIZZONTI.find((o) => o.mesi === orizzonte) ?? ORIZZONTI[2];
  const periodi = granularita === 'MESE' ? scelto.mesi : scelto.settimane;
  // Sei mesi indietro e non tre: da quando i mesi chiusi hanno un saldo vero,
  // il passato non è più solo un confronto — è la storia della cassa, e con tre
  // mesi si vedeva mezzo trimestre. A settimane restano quattro: otto mesi di
  // settimane sarebbero una tabella che non si legge.
  const storico = granularita === 'MESE' ? 6 : 4;
  /**
   * La finestra della previsione, in un posto solo.
   *
   * Il prospetto e il dettaglio di una cella devono chiedere **la stessa**: il
   * dettaglio ricostruisce la previsione con lo stesso calcolo e poi cerca il
   * periodo su cui si è premuto. Con una finestra più corta quel periodo lì
   * dentro non c'è, e su una cella che mostra una cifra vera si apriva
   * «Periodo non trovato» — succedeva a settimane oltre la tredicesima, cioè
   * su quasi tutto l'anno.
   */
  const finestra = `granularita=${granularita}&orizzonte=${periodi}&storico=${storico}`;

  /**
   * Lo scenario sta nell'indirizzo e non nella memoria del componente: così
   * ricaricando si resta sulle stesse ipotesi, e un collegamento può portare
   * qualcuno direttamente sul confronto che gli si vuole far vedere.
   */
  const [parametri, setParametri] = useSearchParams();
  const scenarioAttivo = parametri.get('scenario');
  const [gestisci, setGestisci] = useState(false);
  const scenari = useCarico(() => api.get<Scenario[]>('/api/scenari'), []);

  const conScenario = scenarioAttivo ? `${finestra}&scenario=${scenarioAttivo}` : finestra;
  const { dati, caricamento, errore, ricarica } = useCarico(
    () => api.get<Previsione>(`/api/previsionale?${conScenario}`),
    [granularita, orizzonte, scenarioAttivo],
  );

  if (caricamento) return <Caricamento />;
  if (errore || !dati) return <div className="p-6"><Errore testo={errore ?? 'Dati non disponibili'} riprova={ricarica} /></div>;

  return (
    <div className="pagina space-y-5">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-lg font-semibold tracking-tight">Previsionale di cassa</h1>
          <p className="text-sm text-inchiostro-500">
            Cassa di oggi <span className="num font-medium">{formattaEuro(dati.cassaAttualeCent)}</span>, poi ogni
            periodo apre dove ha chiuso il precedente.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex rounded-md border border-inchiostro-300 bg-white p-0.5">
            {(['MESE', 'SETTIMANA'] as const).map((g) => (
              <button
                key={g}
                onClick={() => setGranularita(g)}
                className={`rounded px-3 py-1.5 text-sm ${granularita === g ? 'bg-inchiostro-900 text-white' : 'text-inchiostro-600'}`}
              >
                {g === 'MESE' ? 'Mensile' : 'Settimanale'}
              </button>
            ))}
          </div>
          <SceltaScenario
            scenari={scenari.dati ?? []}
            attivo={scenarioAttivo}
            onSceglie={(id) => {
              const p = new URLSearchParams(parametri);
              if (id) p.set('scenario', id);
              else p.delete('scenario');
              setParametri(p, { replace: true });
            }}
            onGestisci={() => setGestisci(true)}
          />
          <div className="flex rounded-md border border-inchiostro-300 bg-white p-0.5">
            {ORIZZONTI.map((o) => (
              <button
                key={o.mesi}
                onClick={() => setOrizzonte(o.mesi)}
                className={`rounded px-3 py-1.5 text-sm ${orizzonte === o.mesi ? 'bg-inchiostro-900 text-white' : 'text-inchiostro-600'}`}
              >
                {o.testo}
              </button>
            ))}
          </div>
        </div>
      </header>

      <StradaDelMese previsione={dati} />

      {/* L'allarme guarda i giorni, come il margine nella barra qui sopra: il
          18 settembre il banner diceva in verde «nessun periodo sotto soglia»
          con il minimo giornaliero a −2.180,66 € il 6 ottobre, due righe più
          su. Un allarme più ottimista del numero che gli sta accanto è il
          modo più sicuro di dare una falsa tranquillità. */}
      {dati.primoGiornoSottoSoglia && dati.minimoDiCassa ? (
        <div className="rounded-lg border border-allerta/30 bg-allerta-chiaro p-4 text-sm">
          <p className="font-semibold text-allerta">
            Il {formattaData(dati.primoGiornoSottoSoglia.data, organizzazione.formatoData)} la cassa prevista scende a{' '}
            {formattaEuro(dati.primoGiornoSottoSoglia.saldoCent)}, sotto la soglia di {formattaEuro(dati.sogliaMinimaCent)}
            {/* Il 30/09 la barra dice «saldo previsto 5.761,99 €» e qui «sotto
                soglia»: tornano tutti e due, perché nello stesso giorno le
                uscite si contano prima degli incassi. Va detto, o sembra una
                contraddizione. */}
            {(dati.cammino.find((g) => g.data === dati.primoGiornoSottoSoglia!.data)?.saldoCent ?? 0) >=
              dati.sogliaMinimaCent && (
              <span className="font-normal">
                {' '}
                — per poche ore: le uscite di quel giorno arrivano prima degli incassi, e a fine giornata la cassa
                risale sopra la soglia
              </span>
            )}
          </p>
          <p className="mt-1 text-allerta/90">
            Il punto più basso è <span className="num font-semibold">{formattaEuro(dati.minimoDiCassa.saldoCent)}</span> il{' '}
            {formattaData(dati.minimoDiCassa.data, organizzazione.formatoData)}: mancano{' '}
            <span className="num font-semibold">
              {formattaEuro(dati.sogliaMinimaCent - dati.minimoDiCassa.saldoCent)}
            </span>{' '}
            per restare sopra la soglia. Conta i giorni, non solo le fine mese.
            {dati.fidoCent > 0 && (
              <>
                {' '}
                Il fido affidato è <span className="num">{formattaEuro(dati.fidoCent)}</span>: sotto{' '}
                <span className="num">{formattaEuro(-dati.fidoCent)}</span> la banca non ti segue più.
              </>
            )}
          </p>
        </div>
      ) : dati.primaAllerta ? (
        <div className="rounded-lg border border-allerta/30 bg-allerta-chiaro p-4 text-sm">
          <p className="font-semibold text-allerta">
            Primo periodo sotto soglia: {dati.primaAllerta.etichetta} (
            {formattaEuro(dati.primaAllerta.saldoFinaleCent ?? 0)})
          </p>
          <p className="mt-1 text-allerta/90">
            La soglia minima è {formattaEuro(dati.sogliaMinimaCent)} e si imposta in Impostazioni.
            {dati.fidoCent > 0 && (
              <>
                {' '}
                Il fido affidato è <span className="num">{formattaEuro(dati.fidoCent)}</span>: sotto{' '}
                <span className="num">{formattaEuro(-dati.fidoCent)}</span> la banca non ti segue più.
              </>
            )}
          </p>
        </div>
      ) : (
        <div className="rounded-lg border border-incassi/30 bg-incassi-chiaro p-3 text-sm text-incassi">
          {/* «Da qui in avanti»: i mesi chiusi sotto soglia ci sono (luglio a
              −1.730 €), e sono storia. Il controllo non li guarda, e la frase
              deve dirlo, o accanto a quei numeri sembra falsa. */}
          Nessun giorno, da oggi in avanti, scende sotto la soglia di {formattaEuro(dati.sogliaMinimaCent)}{' '}
          nell’orizzonte scelto.
          {dati.fidoCent > 0 && (
            <> Sotto la soglia hai comunque {formattaEuro(dati.fidoCent)} di fido prima che la banca smetta di seguirti.</>
          )}
        </div>
      )}

      {dati.scaduti.quantita > 0 && (
        // L'arretrato pesa, ma va detto **quanto** pesa: mescolato alle
        // scadenze del periodo un credito fermo da mesi sembrerebbe in regola,
        // e parte di questi documenti potrebbe essere già rientrata e soltanto
        // non riconciliata.
        <div className="rounded-lg border border-attenzione/30 bg-attenzione-chiaro p-4 text-sm">
          <p className="font-medium text-attenzione">
            Il periodo in corso si trascina{' '}
            {plurale(dati.scaduti.quantita, 'documento arretrato', 'documenti arretrati')} dai periodi
            precedenti:{' '}
            <span className="num">{formattaEuro(dati.scaduti.entrateCent)}</span> da incassare e{' '}
            <span className="num">{formattaEuro(dati.scaduti.usciteCent)}</span> da pagare.
          </p>
          <p className="mt-1 text-inchiostro-600">
            <strong>Non entrano nel saldo a fine periodo</strong>: sono per lo più fatture di mesi fa, e parte potrebbe
            essere già rientrata e soltanto non riconciliata. Presumere che si chiudano tutte entro fine mese deciderebbe
            il saldo con la cifra più incerta della pagina.{' '}
            <Link to="/riconciliazione" className="underline underline-offset-2">
              Riconcilia i movimenti
            </Link>{' '}
            e questo numero scende da solo: quello che resta è quello che devi ancora vedere passare in banca.
          </p>
        </div>
      )}

      {scenarioAttivo && scenari.dati?.some((x) => x.id === scenarioAttivo) && (
        // Un prospetto di ipotesi che sembra il prospetto vero è il modo più
        // rapido di prendere una decisione sul mondo sbagliato: va detto in
        // pagina, non solo nel menù a tendina.
        <div className="rounded-lg border border-skin/30 bg-skin/5 px-4 py-3 text-sm">
          <strong>Stai guardando uno scenario:</strong>{' '}
          {scenari.dati.find((x) => x.id === scenarioAttivo)!.nome} —{' '}
          {riassunto(scenari.dati.find((x) => x.id === scenarioAttivo)!)}.{' '}
          <button
            className="underline decoration-dotted underline-offset-2"
            onClick={() => {
              const p = new URLSearchParams(parametri);
              p.delete('scenario');
              setParametri(p, { replace: true });
            }}
          >
            Torna all’effettivo
          </button>
        </div>
      )}

      {gestisci && (
        <GestioneScenari
          scenari={scenari.dati ?? []}
          onChiudi={() => setGestisci(false)}
          onCambiato={(eliminatoId) => {
            scenari.ricarica();
            // Cancellato quello che si stava guardando, si torna ai dati veri
            // invece di restare su un identificativo che non esiste più.
            if (eliminatoId && eliminatoId === scenarioAttivo) {
              const p = new URLSearchParams(parametri);
              p.delete('scenario');
              setParametri(p, { replace: true });
            }
            ricarica();
          }}
        />
      )}

      <Grafico previsione={dati} />
      <Griglia
        previsione={dati}
        formatoData={organizzazione.formatoData}
        // Con lo scenario dentro: aprendo una cella le righe devono spiegare
        // **quella** cifra, non la stessa cella calcolata sui dati veri.
        finestra={conScenario}
        onRicalcola={ricarica}
      />
    </div>
  );
}

/**
 * La strada dalla cassa di oggi a quella di fine mese, in una riga.
 *
 * È il calcolo che il prospetto fa comunque, ma che finora bisognava
 * ricostruire a mente saltando fra le colonne: «oggi ho tanto, devo ancora
 * incassare tanto, devo ancora pagare tanto, quindi finisco a tanto». Sono i
 * due numeri che contano davvero — quanto c'è adesso e quanto resterà — con in
 * mezzo le due cifre che li separano.
 *
 * L'arretrato non è qui: non si sa quando rientra, e metterlo dentro
 * trasformerebbe l'unica cifra affidabile della pagina in una scommessa.
 */
function StradaDelMese({ previsione }: { previsione: Previsione }) {
  const { organizzazione } = useSessione();
  const [simula, setSimula] = useState(false);
  const [importoSpesa, setImportoSpesa] = useState('');
  const [dataSpesa, setDataSpesa] = useState(previsione.cammino[0]?.data ?? '');
  const p = previsione.periodi.find((x) => x.collocazione === 'CORRENTE');
  if (!p || p.saldoFinaleCent === null) return null;
  const data = (iso: string) => formattaData(iso, organizzazione.formatoData);
  /**
   * Da quanti periodi di fila la cassa cala, dal periodo in corso in avanti.
   *
   * Un periodo negativo non è un guaio: è l'IVA, sono le tredicesime, è un
   * acquisto. Diversi di fila sì, ed è l'unica lettura del segno che merita di
   * essere detta — il colore rosso su ogni numero negativo, invece, insegna solo
   * a ignorare il rosso.
   */
  let mesiDiSeguitoInCalo = 0;
  for (const x of previsione.periodi.filter((q) => q.collocazione !== 'PASSATO')) {
    if (x.saldoFinaleCent === null || x.saldoInizialeCent === null) break;
    if (x.saldoFinaleCent - x.saldoInizialeCent >= 0) break;
    mesiDiSeguitoInCalo += 1;
  }

  /**
   * Il margine si misura sul punto **più basso** dell'orizzonte, e giorno per
   * giorno — non sui saldi di fine mese.
   *
   * Settembre può chiudere a 6.883 € e toccare il fondo il 27, se lo stipendio
   * esce prima che i clienti paghino; ottobre può scendere per un F24. Il
   * margine vero è quanto si può togliere a **tutti** i giorni futuri senza che
   * nessuno scenda sotto la soglia: il fondo meno la soglia. Solo da oggi in
   * avanti — i mesi chiusi sotto soglia sono storia.
   *
   * Se il cammino giornaliero non c'è (nessun periodo futuro), si ripiega sul
   * saldo di fine periodo più basso.
   */
  const fineMesePiuBassa = previsione.periodi
    .filter((q) => q.collocazione !== 'PASSATO' && q.saldoFinaleCent !== null)
    .reduce<Previsione['periodi'][number] | null>(
      (piuBasso, q) => (piuBasso === null || q.saldoFinaleCent! < piuBasso.saldoFinaleCent! ? q : piuBasso),
      null,
    );
  const fondo = previsione.minimoDiCassa
    ? { saldoCent: previsione.minimoDiCassa.saldoCent, data: previsione.minimoDiCassa.data }
    : fineMesePiuBassa
      ? { saldoCent: fineMesePiuBassa.saldoFinaleCent!, data: fineMesePiuBassa.fine }
      : null;
  const margineCent = fondo ? fondo.saldoCent - previsione.sogliaMinimaCent : 0;
  const oggiIso = previsione.cammino[0]?.data ?? p.inizio;
  const giorniAlFondo = fondo ? Math.round((Date.parse(fondo.data) - Date.parse(oggiIso)) / 86_400_000) : 0;

  /**
   * Quanto entra e quanto esce da oggi al punto critico, ricavato dal cammino:
   * in ogni giorno il saldo scende di tutte le uscite (fino al minimo del
   * giorno) e poi risale delle entrate.
   */
  const finoAlFondo = { entrateCent: 0, usciteCent: 0 };
  if (fondo) {
    let prima = previsione.cassaAttualeCent;
    for (const g of previsione.cammino) {
      if (g.data > fondo.data) break;
      finoAlFondo.usciteCent += prima - g.minimoCent;
      // Nel giorno critico le entrate arrivano dopo il punto più basso: non lo
      // evitano, quindi non si contano fra quelle già attese prima.
      if (g.data < fondo.data) finoAlFondo.entrateCent += g.saldoCent - g.minimoCent;
      prima = g.saldoCent;
    }
  }

  /**
   * «Posso spendere questi soldi?» La spesa esce il giorno scelto e abbassa
   * tutto quello che viene dopo: il nuovo fondo è la risposta. Lo stesso conto
   * del margine qui sopra, fatto da `simulaSpesa` nel motore.
   */
  const spesaCent = (() => {
    const pulito = importoSpesa.trim().replace(/\./g, '').replace(',', '.');
    const n = Number(pulito);
    return pulito !== '' && Number.isFinite(n) && n > 0 ? Math.round(n * 100) : null;
  })();
  const conSpesa =
    spesaCent !== null && dataSpesa ? simulaSpesa(previsione.cammino, dataSpesa, spesaCent) : null;

  const fine = new Date(p.fine + 'T00:00:00');
  const alGiorno = `${fine.getDate()}/${String(fine.getMonth() + 1).padStart(2, '0')}`;

  const passo = (etichetta: string, cent: number, tono: 'saldo' | 'incassi' | 'uscite', forte = false) => (
    <div className="min-w-36">
      <div className="text-xs text-inchiostro-500">{etichetta}</div>
      <div className={forte ? 'text-lg' : 'text-base'}>
        <Importo cent={cent} tono={tono} grassetto={forte} />
      </div>
    </div>
  );
  const freccia = <div aria-hidden className="pb-1 text-lg text-inchiostro-300">→</div>;

  return (
    <div className="flex flex-wrap items-end gap-x-5 gap-y-3 rounded-lg border border-inchiostro-200 bg-white p-4">
      {passo('Cassa di oggi', previsione.cassaAttualeCent, 'saldo', true)}
      {freccia}
      {passo('Ancora da incassare', p.entrateResidueCent, 'incassi')}
      {freccia}
      {passo('Ancora da pagare', -p.usciteResidueCent, 'uscite')}
      {freccia}
      {passo(`Saldo previsto al ${alGiorno}`, p.saldoFinaleCent, 'saldo', true)}

      {/* La domanda dopo «quanto resta» è sempre «quanto posso impegnare»: è
          quello che resta sopra la soglia che ci si è dati, non il saldo. */}
      <div className="basis-full border-t border-inchiostro-200 pt-3 text-sm">
        {previsione.sogliaMinimaCent > 0 && fondo ? (
          <>
            <div className="flex flex-wrap items-baseline justify-between gap-2">
              {/* «Margine disponibile: −7.180 €» non si legge: un margine
                  negativo non è un margine. Quando manca, si chiama con il suo
                  nome e si dice dove cade. */}
              {margineCent >= 0 ? (
                <div>
                  <span className="text-inchiostro-600">Margine di sicurezza disponibile:</span>{' '}
                  <span className="num font-semibold">+{formattaEuro(margineCent)}</span>
                </div>
              ) : (
                <div>
                  <span className="text-allerta">Deficit previsto rispetto alla soglia:</span>{' '}
                  <span className="num font-semibold text-allerta">{formattaEuro(margineCent)}</span>
                  <span className="mt-0.5 block text-xs text-allerta">
                    Punto critico: {data(fondo.data)}
                    {giorniAlFondo > 0 && <> (fra {plurale(giorniAlFondo, 'giorno', 'giorni')})</>} — saldo previsto{' '}
                    <span className="num">{formattaEuro(fondo.saldoCent)}</span>
                  </span>
                </div>
              )}
              {previsione.cammino.length > 0 && (
                <button className="bottone-secondario px-2 py-1 text-xs" onClick={() => setSimula(!simula)}>
                  {simula ? 'Chiudi la simulazione' : 'Simula una spesa'}
                </button>
              )}
            </div>
            {margineCent >= 0 ? (
              <span className="mt-0.5 block text-xs text-inchiostro-500">
                Il punto più basso previsto è <span className="num">{formattaEuro(fondo.saldoCent)}</span> il{' '}
                {data(fondo.data)}
                {giorniAlFondo > 0 && <> (fra {plurale(giorniAlFondo, 'giorno', 'giorni')})</>}, contando i giorni e
                non solo le fine mese; la soglia è{' '}
                <span className="num">{formattaEuro(previsione.sogliaMinimaCent)}</span>. È un margine previsionale, non
                liquidità da spendere oggi: vale se le previsioni si avverano.
              </span>
            ) : (
              /**
               * Non solo «avrai un problema», ma quanto va spostato e entro
               * quando. Il deficit si colma portando incassi **prima** del
               * punto critico o spostando pagamenti **dopo**: e dire quanti ce
               * ne sono in quella finestra evita di suggerire di rimandare
               * pagamenti che non esistono.
               */
              <div className="mt-2 rounded-md bg-allerta-chiaro/60 p-3 text-xs text-inchiostro-700">
                Per restare sopra la soglia fino al {data(fondo.data)} servono almeno{' '}
                <span className="num font-semibold">{formattaEuro(-margineCent)}</span>:
                <ul className="mt-1 list-disc pl-5">
                  <li>
                    anticipando incassi a prima del {data(fondo.data)}
                    {finoAlFondo.entrateCent > 0 && (
                      <>
                        {' '}
                        — fino a quel giorno ne sono già attesi{' '}
                        <span className="num">{formattaEuro(finoAlFondo.entrateCent)}</span>
                      </>
                    )}
                    ;
                  </li>
                  <li>
                    oppure rimandando a dopo il {data(fondo.data)} pagamenti previsti entro quel giorno: ce ne sono{' '}
                    <span className="num">{formattaEuro(finoAlFondo.usciteCent)}</span>
                    {finoAlFondo.usciteCent < -margineCent && ', non abbastanza da soli'};
                  </li>
                  <li>oppure combinando le due cose.</li>
                </ul>
                <span className="mt-1 block text-inchiostro-500">
                  Rimandare un pagamento lo sposta più avanti, non lo cancella: controlla con «Simula una spesa» che il
                  nuovo punto più basso non cada un mese dopo.
                </span>

                {/* Chi chiamare e cosa spostare, senza andare a cercarlo nello
                    scadenzario: le uscite che scavano il fondo e gli incassi
                    che arrivano subito dopo, troppo tardi. */}
                {previsione.causeDelMinimo &&
                  (previsione.causeDelMinimo.uscite.length > 0 ||
                    previsione.causeDelMinimo.entrateSubitoDopo.length > 0) && (
                    <div className="mt-3 border-t border-allerta/20 pt-2">
                      <p className="font-semibold text-inchiostro-800">
                        Perché scendi a <span className="num">{formattaEuro(fondo.saldoCent)}</span> il {data(fondo.data)}?
                      </p>
                      <table className="mt-1 w-full">
                        <tbody>
                          {previsione.causeDelMinimo.uscite.map((f, i) => (
                            <tr key={`u${i}`}>
                              <td className="num w-24 whitespace-nowrap py-0.5">{data(f.data)}</td>
                              <td className="py-0.5">{f.etichetta ?? '—'}</td>
                              <td className="py-0.5 text-right">
                                <Importo cent={f.importoCent} tono="uscite" />
                              </td>
                            </tr>
                          ))}
                          {previsione.causeDelMinimo.entrateSubitoDopo.length > 0 && (
                            <tr>
                              <td colSpan={3} className="pt-2 text-inchiostro-500">
                                Incassi attesi poco dopo — se arrivassero prima, il buco si chiuderebbe:
                              </td>
                            </tr>
                          )}
                          {previsione.causeDelMinimo.entrateSubitoDopo.map((f, i) => (
                            <tr key={`e${i}`}>
                              <td className="num w-24 whitespace-nowrap py-0.5">{data(f.data)}</td>
                              <td className="py-0.5">{f.etichetta ?? '—'}</td>
                              <td className="py-0.5 text-right">
                                <Importo cent={f.importoCent} tono="incassi" segno />
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
              </div>
            )}

            {simula && (
              <div className="mt-3 rounded-md border border-inchiostro-200 bg-inchiostro-50 p-3">
                <div className="flex flex-wrap items-end gap-3">
                  <label className="block">
                    <span className="etichetta">Quanto vuoi spendere</span>
                    <input
                      className="campo num w-36 text-right"
                      inputMode="decimal"
                      placeholder="0,00"
                      autoFocus
                      value={importoSpesa}
                      onChange={(e) => setImportoSpesa(e.target.value)}
                    />
                  </label>
                  <label className="block">
                    <span className="etichetta">Quando</span>
                    <input
                      type="date"
                      className="campo"
                      min={oggiIso}
                      value={dataSpesa}
                      onChange={(e) => setDataSpesa(e.target.value)}
                    />
                  </label>
                </div>
                {conSpesa && spesaCent !== null && (
                  <p className="mt-2 text-sm">
                    Con questa spesa il punto più basso diventa{' '}
                    <span
                      className={`num font-semibold ${
                        conSpesa.saldoCent < previsione.sogliaMinimaCent ? 'text-allerta' : ''
                      }`}
                    >
                      {formattaEuro(conSpesa.saldoCent)}
                    </span>{' '}
                    il {data(conSpesa.data)}.{' '}
                    {conSpesa.saldoCent >= previsione.sogliaMinimaCent ? (
                      <>
                        Resti sopra la soglia di{' '}
                        <span className="num">{formattaEuro(conSpesa.saldoCent - previsione.sogliaMinimaCent)}</span>.
                      </>
                    ) : (
                      <span className="text-allerta">
                        Scendi sotto la soglia di{' '}
                        <span className="num">{formattaEuro(previsione.sogliaMinimaCent - conSpesa.saldoCent)}</span>
                        {conSpesa.saldoCent < 0 && <>, e sotto zero</>}.
                      </span>
                    )}
                  </p>
                )}
              </div>
            )}
          </>
        ) : (
          <span className="text-inchiostro-500">
            Non hai fissato una soglia minima di cassa: finché è zero, «quanto posso spendere» coincide col saldo
            previsto.{' '}
            <Link to="/impostazioni" className="underline decoration-dotted underline-offset-2">
              Impostane una
            </Link>{' '}
            e qui compare quanto puoi impegnare restando sopra.
          </span>
        )}
        {mesiDiSeguitoInCalo >= 3 && (
          <span className="mt-1 block text-attenzione">
            La cassa cala per {mesiDiSeguitoInCalo} periodi di fila: un periodo negativo capita, diversi di seguito
            sono una direzione.
          </span>
        )}
      </div>

      {p.perFonte.SCADUTI.entrateCent + p.perFonte.SCADUTI.usciteCent > 0 && (
        <p className="basis-full text-xs text-inchiostro-500">
          Fuori da questo conto c'è l'arretrato:{' '}
          <span className="num">{formattaEuro(p.perFonte.SCADUTI.entrateCent)}</span> da incassare e{' '}
          <span className="num">{formattaEuro(p.perFonte.SCADUTI.usciteCent)}</span> da pagare, scaduti prima
          dell'inizio del periodo. Non si sa quando rientrano, quindi non entrano nel saldo previsto.
        </p>
      )}
    </div>
  );
}

function Grafico({ previsione }: { previsione: Previsione }) {
  const serie = previsione.periodi.map((p) => ({
    nome: p.etichetta,
    // La linea corre anche sui periodi chiusi: lì non è una previsione, è la
    // cassa che c'era, ricostruita dai movimenti. Resta `null` solo dove la
    // storia dei conti non arriva.
    saldo: p.saldoFinaleCent === null ? null : p.saldoFinaleCent / 100,
    // Senza l'arretrato, come il saldo che la linea disegna: a settembre le
    // barre contavano 165 mila di fatture passive scadute prima del mese e
    // mostravano 200 mila di uscite, mentre il saldo — giustamente — non le
    // conta. Un grafico le cui barre e la cui linea dicono cose diverse non
    // si legge.
    entrate: (p.entrateCent - p.perFonte.SCADUTI.entrateCent) / 100,
    uscite: -(p.usciteCent - p.perFonte.SCADUTI.usciteCent) / 100,
  }));

  return (
    <Scheda titolo="Andamento" sottotitolo="Barre: entrate e uscite del periodo, senza l’arretrato. Linea: saldo di cassa a fine periodo.">
      <div className="h-96">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={serie} margin={{ top: 8, right: 8, bottom: 0, left: 8 }}>
            <CartesianGrid stroke="var(--color-inchiostro-200)" vertical={false} />
            <XAxis dataKey="nome" tick={{ fontSize: 11, fill: 'var(--color-inchiostro-500)' }} tickLine={false} />
            <YAxis
              tick={{ fontSize: 11, fill: 'var(--color-inchiostro-500)' }}
              tickLine={false}
              axisLine={false}
              tickFormatter={(v) => euroCompatto(v * 100)}
            />
            <Tooltip
              formatter={(v, n) => [formattaEuro(Math.abs(Number(v)) * 100), String(n)]}
              contentStyle={{ fontSize: 12, borderRadius: 6, border: '1px solid var(--color-inchiostro-200)' }}
            />
            <ReferenceLine y={0} stroke="var(--color-inchiostro-400)" />
            <ReferenceLine
              y={previsione.sogliaMinimaCent / 100}
              stroke="var(--color-allerta)"
              strokeDasharray="4 3"
            />
            {/* Il fido è un limite diverso dalla soglia: sotto la soglia sei in
                allerta, sotto il fido la banca non ti segue più. */}
            {previsione.fidoCent > 0 && (
              <ReferenceLine
                y={-previsione.fidoCent / 100}
                stroke="var(--color-uscite)"
                strokeDasharray="2 4"
                label={{ value: 'fine del fido', position: 'insideBottomLeft', fontSize: 10, fill: 'var(--color-uscite)' }}
              />
            )}
            <Bar dataKey="entrate" name="Entrate" fill="var(--color-incassi)" fillOpacity={0.85} />
            <Bar dataKey="uscite" name="Uscite" fill="var(--color-uscite)" fillOpacity={0.85} />
            <Line
              type="monotone"
              dataKey="saldo"
              name="Saldo previsto"
              stroke="var(--color-saldo)"
              strokeWidth={2.5}
              connectNulls={false}
              dot={{ r: 3 }}
            />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </Scheda>
  );
}

/**
 * La riga «Altro» non è una categoria vera: ha un id sintetico, e serve un
 * segno riconoscibile perché il dettaglio non lo mandi al database come se
 * fosse un identificativo.
 */
const SUFFISSO_ALTRO = '#altro';

interface RigaGriglia {
  categoria: Categoria;
  livello: number;
  perPeriodo: Map<string, Quote>;
  totale: number;
  /**
   * Come chiamarla fuori dalla griglia. In tabella «Altro» si capisce perché
   * sta rientrato sotto il suo padre; nel titolo di una finestra da solo non
   * vuol dire niente.
   */
  nomeEsteso?: string;
}

/**
 * Da dove viene un numero, in tre gradi di certezza. Non è una distinzione
 * accademica: davanti a 4.600 € cambia tutto sapere se sono già usciti, se
 * qualcuno ha compilato il modulo che li farà uscire il 16, o se è Mastro ad
 * averli stimati dalle fatture.
 */
interface Quote {
  /** Cassa già transitata in banca. */
  movimenti: number;
  /** Fatture: dichiarate, datate, non ancora incassate o pagate. */
  documenti: number;
  /**
   * Fatture con la data di cassa già passata, trascinate sul periodo in corso.
   *
   * Sta a sé e non con i movimenti: finiva lì per una `else` che raccoglieva
   * tutto ciò che non era documento, F24 o piano, e la colonna «in corso» — che
   * il prospetto descrive come «già transitato in banca» — contava come
   * incassato ciò che invece è ancora da incassare. Sui Ricavi di settembre
   * dichiarava 68.021,79 € già passati in banca quando in banca non era passato
   * niente, e la percentuale accanto diceva «manca il 39%» invece del 100%.
   */
  scaduti: number;
  /** F24 registrati: come le fatture, un impegno dichiarato con una scadenza. */
  f24: number;
  /** Stime: budget e ricorrenti — ciò che si prevede oltre a documenti e moduli. */
  piano: number;
}
const quoteVuote = (): Quote => ({ movimenti: 0, documenti: 0, scaduti: 0, f24: 0, piano: 0 });
const totaleQuote = (q: Quote) => q.movimenti + q.documenti + q.scaduti + q.f24 + q.piano;

/** Quale cella si sta guardando: periodo, voce e verso. */
interface CellaScelta {
  periodo: string;
  etichettaPeriodo: string;
  /**
   * La voce **e le sue figlie**: la riga di una categoria con sottovoci mostra
   * il totale del ramo, e chiedere il dettaglio della sola voce padre darebbe
   * un elenco che non somma alla cifra su cui si è premuto.
   */
  ramo: string[];
  nomeCategoria: string;
  verso: 'ENTRATA' | 'USCITA';
  /**
   * Quale cifra è stata premuta. Nel mese in corso sono tre, e sono fatte di
   * cose diverse: «in corso» è il denaro già transitato in banca, «previsto»
   * ciò che deve ancora essere pagato o incassato nel periodo, «arretrato» ciò
   * che si trascina da prima. Aprire la stessa finestra per tutte sarebbe il
   * modo migliore per non fidarsi di nessuna: ognuna mostra le righe che
   * compongono **la sua** cifra, e nient'altro.
   */
  parte: 'IN_CORSO' | 'PREVISTO' | 'RESIDUO' | 'ARRETRATO' | 'TUTTO';
}

function Griglia({
  previsione,
  formatoData,
  finestra,
  onRicalcola,
}: {
  previsione: Previsione;
  formatoData: Parameters<typeof formattaData>[1];
  /** La finestra con cui è stato chiesto il prospetto: la cella la ripassa al dettaglio. */
  finestra: string;
  /** Rifà il prospetto dopo che è stato scritto un budget. */
  onRicalcola: () => void;
}) {
  const [espansi, setEspansi] = useState<Set<string>>(new Set());

  /**
   * La tabella carica sei mesi chiusi ma si apre mostrandone tre: la domanda di
   * tutti i giorni riguarda il mese in corso e i prossimi, e sei colonne di
   * storico davanti spingevano settembre fuori dallo schermo. Gli altri tre
   * sono lì, a un gesto di scorrimento. Si riposiziona solo quando cambiano i
   * periodi — orizzonte o granularità — non a ogni ricarica dei dati, che
   * riporterebbe indietro chi sta guardando lo storico.
   */
  const scorrimento = useRef<HTMLDivElement>(null);
  const chiaviPeriodi = previsione.periodi.map((p) => p.chiave).join(',');
  useEffect(() => {
    const box = scorrimento.current;
    if (!box) return;
    const corrente = previsione.periodi.findIndex((p) => p.collocazione === 'CORRENTE');
    const primo = previsione.periodi[Math.max(0, corrente - 3)];
    const colonna = box.querySelector<HTMLElement>(`th[data-periodo="${primo?.chiave}"]`);
    const voce = box.querySelector<HTMLElement>('th[data-colonna="voce"]');
    if (!colonna || !voce) return;
    box.scrollLeft = Math.max(0, colonna.offsetLeft - voce.offsetWidth);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chiaviPeriodi]);
  const [cella, setCella] = useState<CellaScelta | null>(null);
  const categorie = previsione.categorie ?? [];

  /**
   * I budget **grezzi**, non quelli che si vedono nel prospetto.
   *
   * La cifra in cella è una somma — fatture, F24, piano — e il piano a sua
   * volta pesa solo per quanto eccede ciò che è già successo. Scriverci sopra
   * il totale trasformerebbe in budget anche le fatture che ci sono dentro.
   * Qui serve il numero così com'è scritto nella sua riga.
   */
  const mesi = previsione.periodi.map((p) => p.chiave).filter((c) => /^\d{4}-\d{2}$/.test(c));
  const budget = useCarico(
    () =>
      mesi.length === 0
        ? Promise.resolve([] as VoceBudget[])
        : api.get<VoceBudget[]>(`/api/budget?da=${mesi[0]}&a=${mesi[mesi.length - 1]}`),
    [mesi[0], mesi[mesi.length - 1]],
  );
  const [budgetDaScrivere, setBudgetDaScrivere] = useState<{
    categoriaId: string;
    nomeCategoria: string;
    verso: 'ENTRATA' | 'USCITA';
    mese: string;
    etichettaMese: string;
    importoCent: number;
  } | null>(null);

  const budgetDi = useMemo(() => {
    const m = new Map<string, number>();
    for (const v of budget.dati ?? []) m.set(`${v.categoriaId}|${v.mese}`, v.importoCent);
    return m;
  }, [budget.dati]);

  // Se nel periodo in corso non è passato niente in banca, la colonna del già
  // transitato è tutta a zero. Non è un difetto del calcolo — è l'estratto
  // conto che non è ancora stato caricato — ma va detto, perché una colonna di
  // zeri senza spiegazione si legge come un guasto.
  const periodoCorrente = previsione.periodi.find((p) => p.collocazione === 'CORRENTE') ?? null;
  const bancaIndietro =
    periodoCorrente !== null &&
    periodoCorrente.perFonte.MOVIMENTI.entrateCent === 0 &&
    periodoCorrente.perFonte.MOVIMENTI.usciteCent === 0;

  const { righe, figliDi } = useMemo(() => {
    const perId = new Map(categorie.map((c) => [c.id, c]));
    const figliDi = new Map<string | null, Categoria[]>();
    for (const c of categorie) figliDi.set(c.padreId, [...(figliDi.get(c.padreId) ?? []), c]);

    // I contributi arrivano per categoria: qui si sommano anche sui rami, così
    // il padre mostra il totale del ramo mentre i valori memorizzati restano
    // sempre i propri.
    /**
     * I tre gradi di certezza di un numero, tenuti distinti perché sono cose
     * diverse: **movimenti** è cassa già transitata, **impegni** è dichiarato e
     * datato ma non ancora uscito (fatture, F24), **piano** è stimato (budget,
     * ricorrenti, IVA calcolata dai documenti).
     *
     * L'IVA calcolata sta con il piano, non con gli impegni: è una stima che
     * Mastro ricava dai documenti. Un F24 registrato è l'opposto — qualcuno l'ha
     * compilato, ha una scadenza sua e conosce la compensazione — e sta con le
     * fatture. Quando viene versato passa da solo fra i movimenti, e la
     * progressione si chiude: stimato, dichiarato, avvenuto.
     */
    const proprio = new Map<string, Map<string, Quote>>();
    for (const p of previsione.periodi) {
      for (const c of p.contributi) {
        const id = c.categoriaId ?? '(nessuna)';
        const perPeriodo = proprio.get(id) ?? new Map();
        const v = perPeriodo.get(p.chiave) ?? quoteVuote();
        if (c.fonte === 'DOCUMENTI') v.documenti += c.importoCent;
        // Gli arretrati sono documenti, non cassa: se cadessero nell'`else`
        // insieme ai movimenti, la colonna del già passato li conterebbe come
        // incassati.
        else if (c.fonte === 'SCADUTI') v.scaduti += c.importoCent;
        else if (c.fonte === 'F24') v.f24 += c.importoCent;
        // L'IVA è una previsione come il piano, non qualcosa di già avvenuto:
        // finire fra i movimenti la farebbe sembrare cassa già uscita.
        else if (c.fonte === 'PIANO') v.piano += c.importoCent;
        else v.movimenti += c.importoCent;
        perPeriodo.set(p.chiave, v);
        proprio.set(id, perPeriodo);
      }
    }

    const conRamo = (id: string): Map<string, Quote> => {
      const somma = new Map<string, Quote>();
      const accumula = (m: Map<string, Quote> | undefined) => {
        if (!m) return;
        for (const [k, v] of m) {
          const s = somma.get(k) ?? quoteVuote();
          s.documenti += v.documenti;
          s.scaduti += v.scaduti;
          s.f24 += v.f24;
          s.piano += v.piano;
          s.movimenti += v.movimenti;
          somma.set(k, s);
        }
      };
      accumula(proprio.get(id));
      for (const f of figliDi.get(id) ?? []) accumula(conRamo(f.id));
      return somma;
    };

    const righe: RigaGriglia[] = [];
    const scendi = (padre: string | null, livello: number) => {
      for (const c of (figliDi.get(padre) ?? []).sort((a, b) => a.ordine - b.ordine)) {
        const perPeriodo = conRamo(c.id);
        const totale = [...perPeriodo.values()].reduce((t, v) => t + totaleQuote(v), 0);
        righe.push({ categoria: c, livello, perPeriodo, totale });
        const haFigli = (figliDi.get(c.id) ?? []).length > 0;
        if (haFigli && espansi.has(c.id)) {
          scendi(c.id, livello + 1);
          // «Altro» è un secchio reale, non un residuo calcolato: sono i valori
          // messi direttamente sul padre. Sempre visibile quando ci sono figli.
          const suoi = proprio.get(c.id) ?? new Map();
          righe.push({
            categoria: { ...c, id: `${c.id}${SUFFISSO_ALTRO}`, nome: 'Altro', padreId: c.id },
            livello: livello + 1,
            perPeriodo: suoi,
            totale: [...suoi.values()].reduce((t, v) => t + totaleQuote(v), 0),
            nomeEsteso: `${c.nome} · Altro`,
          });
        }
      }
    };
    scendi(null, 0);
    return { righe, figliDi, perId };
  }, [categorie, previsione.periodi, espansi]);

  const entrate = righe.filter((r) => r.categoria.verso === 'ENTRATA');
  const uscite = righe.filter((r) => r.categoria.verso === 'USCITA');

  return (
    <Scheda
      titolo="Dettaglio per periodo"
      sottotitolo="I periodi chiusi mostrano solo l’effettivo, quelli futuri solo la previsione. Il periodo in corso si apre in cinque colonne: quanto è già passato in banca, quanto il mese si aspetta in tutto, quanto di quel totale deve ancora muoversi, quanto è arretrato trascinato da prima, e quanta parte è già fatta. Le stime del budget pesano dai mesi futuri."
      senzaPadding
    >
      {/* Una colonna tutta a zero è il numero giusto quando in banca non è
          ancora passato niente, ma senza dire da quando i dati si fermano
          sembra un guasto — e chi guarda smette di fidarsi anche del resto. */}
      {bancaIndietro && (
        <div className="border-b border-attenzione/30 bg-attenzione-chiaro px-4 py-3 text-sm text-attenzione">
          La colonna <strong>«in corso»</strong> è vuota perché in banca non risulta ancora nessun movimento di{' '}
          {periodoCorrente!.etichetta}
          {previsione.ultimoMovimentoIl ? (
            <>
              : l’ultimo caricato è del{' '}
              <span className="num">{formattaData(previsione.ultimoMovimentoIl, formatoData)}</span>.
            </>
          ) : (
            '.'
          )}{' '}
          <Link to="/importazioni" className="underline decoration-dotted underline-offset-2">
            Carica l’estratto conto
          </Link>{' '}
          e la colonna si riempie. Le altre colonne non ne risentono: fatture, F24 e budget sono al loro posto.
        </div>
      )}
      <div ref={scorrimento} className="overflow-x-auto">
        <table className="dati">
          <thead>
            <tr>
              <th rowSpan={2} data-colonna="voce" className="sticky left-0 z-10 min-w-56 bg-inchiostro-100">
                Voce
              </th>
              {previsione.periodi.map((p) => (
                <th
                  key={p.chiave}
                  data-periodo={p.chiave}
                  colSpan={p.collocazione === 'CORRENTE' ? COLONNE_MESE_IN_CORSO : 1}
                  className={`min-w-28 text-right ${p.collocazione === 'CORRENTE' ? 'bg-inchiostro-200' : ''}`}
                >
                  {p.etichetta}
                </th>
              ))}
            </tr>
            {/* Il periodo in corso si apre in quattro colonne: leggere «già passato
                / previsto / quanto manca» dentro una cella sola costringeva a
                districare tre numeri appiccicati. */}
            <tr>
              {previsione.periodi.map((p) =>
                p.collocazione === 'CORRENTE' ? (
                  <Fragment key={p.chiave}>
                    <th
                      className="bg-inchiostro-200 text-right text-[10px] font-normal normal-case text-inchiostro-600"
                      title="Quanto è già transitato in banca dall'inizio del periodo."
                    >
                      in corso
                    </th>
                    <th
                      className="bg-inchiostro-200 text-right text-[10px] font-normal normal-case text-inchiostro-600"
                      title="Quello che il mese si aspetta in tutto, già passato compreso: movimenti in banca, fatture in scadenza, F24 e scadenze fisse come gli stipendi. Le stime del budget no: nel mese in corso contano i fatti. L'arretrato è nella colonna accanto."
                    >
                      totale mese
                    </th>
                    <th
                      className="bg-inchiostro-200 text-right text-[10px] font-normal normal-case text-inchiostro-600"
                      title="Quanto del totale deve ancora muoversi: totale del mese meno quello che è già passato in banca. Non scende mai sotto zero — se si è già incassato più del previsto, non resta niente da incassare e quel di più è già dentro la cassa di oggi."
                    >
                      residuo
                    </th>
                    <th
                      className="bg-inchiostro-200 text-right text-[10px] font-normal normal-case text-attenzione"
                      title="Documenti con la data di cassa di un periodo precedente. Si vedono qui ma non entrano nel saldo a fine periodo."
                    >
                      arretrato
                    </th>
                    <th
                      className="bg-inchiostro-200 text-right text-[10px] font-normal normal-case text-inchiostro-600"
                      title="Quanta parte del previsto del mese è già passata in banca. A metà mese è la domanda vera: a che punto siamo."
                    >
                      già fatto
                    </th>
                  </Fragment>
                ) : (
                  <th
                    key={p.chiave}
                    className="text-right text-[10px] font-normal normal-case text-inchiostro-500"
                  >
                    {p.collocazione === 'PASSATO' ? 'effettivo' : 'previsione'}
                  </th>
                ),
              )}
            </tr>
          </thead>
          <tbody>
            {/* La cassa con cui il periodo apre, in cima e non in fondo: è da
                lì che si comincia a leggere, e finora non si vedeva da nessuna
                parte — il riporto esisteva nel calcolo ma non sulla pagina, e
                senza di esso ogni mese sembrava partire dal nulla. */}
            <tr className="bg-inchiostro-50">
              <td className="sticky left-0 z-10 bg-inchiostro-50 font-semibold">Cassa a inizio periodo</td>
              {previsione.periodi.map((p) => (
                <CellaDelPeriodo key={p.chiave} periodo={p}>
                  {p.saldoInizialeCent === null ? (
                    <span
                      className="text-inchiostro-300"
                      title="Prima di questa data non tutti i conti hanno la loro storia: un saldo qui sarebbe pulito e falso."
                    >
                      —
                    </span>
                  ) : (
                    <span
                      title={
                        p.collocazione === 'FUTURO'
                          ? 'È il saldo con cui ha chiuso il periodo precedente.'
                          : 'Cassa che c’era davvero il primo giorno del periodo, ricostruita dai movimenti.'
                      }
                    >
                      <Importo cent={p.saldoInizialeCent} tono="saldo" grassetto={p.collocazione !== 'FUTURO'} />
                    </span>
                  )}
                </CellaDelPeriodo>
              ))}
            </tr>
            <SezioneRighe
              titolo="Entrate"
              righe={entrate}
              periodi={previsione.periodi}
              espansi={espansi}
              setEspansi={setEspansi}
              figliDi={figliDi}
              tono="incassi"
              onApriCella={setCella}
              modificabile={previsione.granularita === 'MESE'}
              budgetDi={budgetDi}
              onModificaBudget={setBudgetDaScrivere}
            />
            <Totale
              etichetta="Totale entrate"
              periodi={previsione.periodi}
              valore={(p) => p.entrateCent}
              effettivo={(p) => p.perFonte.MOVIMENTI.entrateCent}
              arretrato={(p) => p.perFonte.SCADUTI.entrateCent}
              tono="incassi"
            />

            <SezioneRighe
              titolo="Uscite"
              righe={uscite}
              periodi={previsione.periodi}
              espansi={espansi}
              setEspansi={setEspansi}
              figliDi={figliDi}
              tono="uscite"
              onApriCella={setCella}
              modificabile={previsione.granularita === 'MESE'}
              budgetDi={budgetDi}
              onModificaBudget={setBudgetDaScrivere}
            />
            <Totale
              etichetta="Totale uscite"
              periodi={previsione.periodi}
              valore={(p) => p.usciteCent}
              effettivo={(p) => p.perFonte.MOVIMENTI.usciteCent}
              arretrato={(p) => p.perFonte.SCADUTI.usciteCent}
              tono="uscite"
            />

            <tr className="border-t-2 border-inchiostro-300">
              <td className="sticky left-0 z-10 bg-white font-semibold">
                Variazione di cassa
                <span className="block text-xs font-normal text-inchiostro-500">generata o assorbita nel periodo</span>
              </td>
              {previsione.periodi.map((p) => {
                /**
                 * Quanto la cassa cambia nel periodo: **la chiusura meno
                 * l'apertura**, non la somma delle colonne.
                 *
                 * Era il flusso netto di entrate e uscite, e non tornava con i
                 * saldi che ha sopra e sotto: ci stava dentro l'arretrato (che
                 * nel saldo non entra) e ne restavano fuori i giroconti (che
                 * nella cassa entrano eccome). Un numero che non quadra con i
                 * due che gli stanno accanto non lo si guarda due volte.
                 */
                const variazione =
                  p.saldoFinaleCent === null || p.saldoInizialeCent === null
                    ? null
                    : p.saldoFinaleCent - p.saldoInizialeCent;
                return (
                  <CellaDelPeriodo key={p.chiave} periodo={p}>
                    {variazione === null ? (
                      <span className="text-inchiostro-300">—</span>
                    ) : (
                      <span
                        title={
                          // Un mese negativo non è un guaio di per sé: è l'IVA,
                          // sono le tredicesime, è un acquisto grosso. Il nome
                          // lo dice invece di allarmare con un colore.
                          variazione >= 0
                            ? `Cassa generata: il periodo chiude con ${formattaEuro(variazione)} in più di quanto ha aperto.`
                            : `Cassa assorbita: il periodo chiude con ${formattaEuro(-variazione)} in meno di quanto ha aperto. Non è di per sé un guaio — l'IVA, le tredicesime o un acquisto grosso fanno questo effetto — conta se si ripete o se porta il saldo sotto la soglia.`
                        }
                      >
                        <Importo cent={variazione} tono="neutro" segno />
                        <span className="ml-1 text-xs text-inchiostro-500">
                          {variazione >= 0 ? 'generata' : 'assorbita'}
                        </span>
                      </span>
                    )}
                  </CellaDelPeriodo>
                );
              })}
            </tr>
            {/* Un giroconto ha sempre due gambe e si compensa da solo: sparisce
                senza lasciare traccia qui dentro, ed è giusto così — i soldi si
                spostano, non si creano. Quello che resta in questa riga è
                un'anomalia: movimenti esclusi a cui manca la contropartita,
                cioè denaro uscito o entrato davvero che il prospetto non conta.
                Su Erretielle a giugno erano +3.550 € rientrati da uno storno e
                due insoluti SEPA. La riga compare solo quando il conto non
                torna, e serve a far tornare la colonna dei saldi. */}
            {previsione.periodi.some((p) => p.movimentiEsclusiCent !== 0) && (
              <tr>
                <td
                  className="sticky left-0 z-10 bg-white text-xs text-inchiostro-600"
                  title="I giroconti hanno due gambe e si compensano da soli: qui non compaiono. Questo è quello che avanza — movimenti esclusi dal prospetto a cui manca la contropartita, cioè soldi che si sono mossi davvero senza entrare nel flusso. Da guardare: quasi sempre è un'esclusione fatta su una gamba sola."
                >
                  esclusi senza contropartita
                </td>
                {previsione.periodi.map((p) => (
                  <td key={p.chiave} colSpan={p.collocazione === 'CORRENTE' ? COLONNE_MESE_IN_CORSO : 1} className="text-right text-xs">
                    {p.movimentiEsclusiCent === 0 ? (
                      <span className="text-inchiostro-300">—</span>
                    ) : (
                      <span className="num text-inchiostro-500">{formattaEuro(p.movimentiEsclusiCent)}</span>
                    )}
                  </td>
                ))}
              </tr>
            )}
            <tr className="bg-inchiostro-50">
              <td className="sticky left-0 z-10 bg-inchiostro-50 font-semibold">Saldo a fine periodo</td>
              {previsione.periodi.map((p) => (
                // Il saldo è un numero solo anche nel periodo in corso: non si
                // divide in «già successo» e «previsto», è la fotografia di
                // dove si arriva.
                <CellaDelPeriodo key={p.chiave} periodo={p}>
                  {p.saldoFinaleCent === null ? (
                    <span
                      className="text-inchiostro-300"
                      title="Prima di questa data non tutti i conti hanno la loro storia: un saldo qui sarebbe pulito e falso."
                    >
                      —
                    </span>
                  ) : (
                    <span
                      className={p.inAllerta ? 'rounded bg-allerta-chiaro px-1.5 py-0.5' : ''}
                      title={
                        p.collocazione === 'PASSATO'
                          ? 'Cassa che c’era davvero alla fine del periodo, ricostruita dai movimenti. Apre il periodo dopo.'
                          : // Il conto per intero, con i segni: è la domanda «come ci
                            // sei arrivato?», e la risposta deve stare sul numero.
                            `${formattaEuro(p.saldoInizialeCent ?? 0)} di apertura` +
                              (p.collocazione === 'CORRENTE'
                                ? ` + ${formattaEuro(
                                    p.perFonte.MOVIMENTI.entrateCent - p.perFonte.MOVIMENTI.usciteCent,
                                  )} già passati in banca = ${formattaEuro(previsione.cassaAttualeCent)} di cassa oggi`
                                : '') +
                              ` + ${formattaEuro(p.entrateResidueCent)} ancora da incassare − ${formattaEuro(
                                p.usciteResidueCent,
                              )} ancora da pagare = ${formattaEuro(p.saldoFinaleCent ?? 0)}. Apre il periodo dopo.`
                      }
                    >
                      {/* Il passato è un fatto, il futuro una previsione: si
                          distinguono a vista, non solo nel suggerimento. */}
                      <Importo
                        cent={p.saldoFinaleCent}
                        tono="saldo"
                        grassetto={p.collocazione !== 'FUTURO'}
                      />
                    </span>
                  )}
                </CellaDelPeriodo>
              ))}
            </tr>
          </tbody>
        </table>
      </div>
      <div className="flex flex-wrap gap-4 border-t border-inchiostro-200 px-4 py-3 text-xs text-inchiostro-500">
        <span><Etichetta testo="doc" tono="info" /> fatture emesse o ricevute, per il residuo non ancora incassato</span>
        <span>
          <Etichetta testo="piano" /> budget e ricorrenti, per quanto eccedono i documenti
        </span>
        <span>
          <Etichetta testo="scaduti" tono="attenzione" /> documenti con la data di cassa già passata: si vedono nel
          periodo in corso ma non entrano nel saldo
        </span>
        <span className="basis-full text-inchiostro-500">
          Le voci <strong>IVA su vendite</strong> e <strong>IVA su acquisti</strong> tengono la quota d’imposta fuori
          dalle altre, così nelle categorie resta l’imponibile. Valgono solo in previsione: nei periodi già chiusi la
          colonna mostra i movimenti bancari, che arrivano in un importo solo e non si possono dividere.
        </span>
        <span><Etichetta testo="eff" tono="ok" /> già transitato in banca</span>
        <span className="basis-full text-inchiostro-500">
          I saldi dei periodi <strong>chiusi</strong> sono la cassa che c’era davvero, ricostruita dai movimenti; da
          oggi in poi sono previsione. Ogni periodo apre dove ha chiuso il precedente.
        </span>
      </div>

      {cella && (
        <DialogoDettaglio cella={cella} finestra={finestra} formatoData={formatoData} onChiudi={() => setCella(null)} />
      )}

      {budgetDaScrivere && (
        <NuovaPrevisione
          {...budgetDaScrivere}
          // Quanti mesi restano da qui alla fine dell'orizzonte: serve a dire
          // cosa produrrà una voce ricorrente, invece di farlo scoprire dopo.
          mesiNellOrizzonte={
            previsione.periodi.filter((p) => p.collocazione !== 'PASSATO' && p.chiave >= budgetDaScrivere.mese).length
          }
          inCorso={budgetDaScrivere.mese === periodoCorrente?.chiave}
          onChiudi={() => setBudgetDaScrivere(null)}
          onSalvato={() => {
            // Il prospetto si rifà davvero: il budget entra nel calcolo, e un
            // numero che resta quello di prima dopo aver salvato è il modo più
            // rapido di far dubitare di tutta la pagina.
            budget.ricarica();
            onRicalcola();
          }}
        />
      )}
    </Scheda>
  );
}

/**
 * Una previsione scritta dove si vede il buco.
 *
 * Finora budget e ricorrenti stavano su un'altra pagina, e la decisione la si
 * prende guardando il prospetto: si vede che a novembre manca l'IVA e la si
 * vuole scrivere lì, non altrove ricordandosi la cifra.
 *
 * **Propone una riga ricorrente, non un numero in una casella**, ed è la
 * differenza che conta. Quasi tutto ciò che si vuole prevedere si ripete —
 * l'IVA, l'affitto, i canoni — e con il solo budget mensile bisognerebbe
 * riscrivere la stessa cifra dodici volte, dimenticandosene alla tredicesima.
 * La pagina Budget lo dice da sempre: «una riga con un nome, non un numero
 * posato su una casella». Il singolo mese resta possibile, ma è l'eccezione.
 *
 * **Non si scrive sopra il totale della cella.** Quella cifra è una somma di
 * fatture, F24 e piano; qui si scrive solo la previsione, perché scriverci
 * sopra il totale trasformerebbe in previsione anche le fatture che ci sono
 * già dentro. È la ragione per cui la matita è un gesto a sé e non un clic sul
 * numero: il numero e ciò che si modifica non sono la stessa cosa.
 */
function NuovaPrevisione({
  categoriaId,
  nomeCategoria,
  verso,
  mese,
  etichettaMese,
  importoCent,
  mesiNellOrizzonte,
  inCorso,
  onChiudi,
  onSalvato,
}: {
  categoriaId: string;
  nomeCategoria: string;
  verso: 'ENTRATA' | 'USCITA';
  mese: string;
  etichettaMese: string;
  /** Il budget già scritto per quel mese, se c'è. */
  importoCent: number;
  /** Quanti mesi restano nell'orizzonte mostrato: serve a dire cosa produrrà. */
  mesiNellOrizzonte: number;
  /** Se il mese scelto è quello in corso: lì una stima non pesa, e va detto. */
  inCorso: boolean;
  onChiudi: () => void;
  onSalvato: () => void;
}) {
  // Se un budget per quel mese c'è già, si sta correggendo quello: cambiargli
  // forma sotto le mani sarebbe una sorpresa. Altrimenti si parte da ciò che
  // serve quasi sempre, cioè una riga che si ripete.
  const [ricorrente, setRicorrente] = useState(importoCent === 0);
  const [valore, setValore] = useState(importoCent === 0 ? '' : (importoCent / 100).toFixed(2));
  const [nome, setNome] = useState(nomeCategoria);
  const [giorno, setGiorno] = useState('1');
  // Dalla matita si scrive quasi sempre una previsione — «mi aspetto 20 mila di
  // ricavi» — e raramente una scadenza. Lo stipendio si sceglie apposta.
  const [stima, setStima] = useState(true);
  const [salvo, setSalvo] = useState(false);
  const [errore, setErrore] = useState<string | null>(null);

  const cent = (() => {
    const pulito = valore.trim().replace(/\./g, '').replace(',', '.');
    if (pulito === '') return 0;
    const n = Number(pulito);
    return Number.isFinite(n) && n >= 0 ? Math.round(n * 100) : null;
  })();

  const salva = async () => {
    if (cent === null) {
      setErrore('Scrivi un importo, oppure lascia vuoto per togliere la previsione.');
      return;
    }
    if (ricorrente && !nome.trim()) {
      setErrore('Dai un nome alla voce: è come la ritroverai nella pagina Budget.');
      return;
    }
    setSalvo(true);
    try {
      if (ricorrente) {
        await api.post('/api/ricorrenti', {
          nome: nome.trim(),
          categoriaId,
          verso,
          passoMesi: 1,
          primoMese: mese,
          giornoDelMese: Number(giorno) || 1,
          natura: stima ? 'STIMA' : 'SCADENZA',
          importoCent: cent,
          crescitaPercentuale: 0,
        });
      } else {
        await api.put('/api/budget', { categoriaId, mese, importoCent: cent });
      }
      onSalvato();
      onChiudi();
    } catch (e) {
      setErrore(e instanceof Error ? e.message : 'Non salvato.');
      setSalvo(false);
    }
  };

  return (
    <Dialogo aperto titolo={`Previsione · ${nomeCategoria} · ${etichettaMese}`} onChiudi={onChiudi} larghezza="max-w-lg">
      <label className="block">
        <span className="etichetta">Importo</span>
        <input
          className="campo num text-right"
          inputMode="decimal"
          autoFocus
          placeholder="0,00"
          value={valore}
          onChange={(e) => { setValore(e.target.value); setErrore(null); }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') void salva();
            if (e.key === 'Escape') onChiudi();
          }}
        />
      </label>

      <fieldset className="mt-4 space-y-2">
        <legend className="etichetta">Per quanto vale</legend>
        <label className="flex items-start gap-2 text-sm">
          <input type="radio" className="mt-1" checked={ricorrente} onChange={() => setRicorrente(true)} />
          <span>
            <strong>Ogni mese</strong>, da {etichettaMese} in avanti
            {cent !== null && cent > 0 && mesiNellOrizzonte > 0 && (
              <span className="block text-xs text-inchiostro-500">
                {mesiNellOrizzonte} mesi nell’orizzonte mostrato ·{' '}
                <span className="num">{formattaEuro(cent * mesiNellOrizzonte)}</span> in tutto
              </span>
            )}
          </span>
        </label>
        <label className="flex items-start gap-2 text-sm">
          <input type="radio" className="mt-1" checked={!ricorrente} onChange={() => setRicorrente(false)} />
          <span>
            <strong>Solo {etichettaMese}</strong>
            <span className="block text-xs text-inchiostro-500">Un fatto isolato: un conguaglio, una tantum.</span>
          </span>
        </label>
      </fieldset>

      {ricorrente && (
        <div className="mt-3 grid gap-3 sm:grid-cols-[1fr_auto]">
          <label className="block">
            <span className="etichetta">Nome della voce</span>
            <input className="campo" value={nome} onChange={(e) => setNome(e.target.value)} />
          </label>
          <label className="block">
            <span className="etichetta">Il giorno</span>
            <input
              className="campo num w-20 text-right"
              inputMode="numeric"
              value={giorno}
              onChange={(e) => setGiorno(e.target.value.replace(/\D/g, '').slice(0, 2))}
            />
          </label>
        </div>
      )}

      {ricorrente && (
        <fieldset className="mt-3 space-y-2">
          <legend className="etichetta">Che cos’è</legend>
          <label className="flex items-start gap-2 text-sm">
            <input type="radio" className="mt-1" checked={stima} onChange={() => setStima(true)} />
            <span>
              <strong>Una stima</strong>
              <span className="block text-xs text-inchiostro-500">
                Quanto ti aspetti: ricavi, costi variabili. Pesa dai mesi futuri.
              </span>
            </span>
          </label>
          <label className="flex items-start gap-2 text-sm">
            <input type="radio" className="mt-1" checked={!stima} onChange={() => setStima(false)} />
            <span>
              <strong>Una scadenza fissa</strong>
              <span className="block text-xs text-inchiostro-500">
                Un pagamento che arriverà comunque: stipendi, rate, affitto. Pesa anche sul mese in corso.
              </span>
            </span>
          </label>
        </fieldset>
      )}

      {inCorso && (!ricorrente || stima) && (
        <p className="mt-3 rounded bg-attenzione-chiaro px-3 py-2 text-xs text-attenzione">
          {etichettaMese} è il mese in corso: una stima non pesa su questo mese, perché qui contano gli incassi e le
          fatture che ci sono già.{ricorrente ? ' Varrà dal mese prossimo.' : ''}
        </p>
      )}

      <p className="mt-3 text-xs text-inchiostro-500">
        {ricorrente
          ? 'Diventa una voce della pagina Budget, dove si può fermare, cambiare di importo o far crescere negli anni.'
          : 'Resta un budget del solo mese scelto.'}{' '}
        In ogni caso è <strong>solo la previsione</strong>, non il totale della cella: nel prospetto non si somma
        alle fatture della voce, pesa solo per quanto le supera.
      </p>

      {errore && <p className="mt-2 text-sm text-allerta">{errore}</p>}
      <div className="mt-4 flex justify-end gap-2">
        <button className="bottone-secondario" onClick={onChiudi} disabled={salvo}>Annulla</button>
        <button className="bottone-primario" onClick={() => void salva()} disabled={salvo}>
          {salvo ? 'Salvo…' : 'Salva'}
        </button>
      </div>
    </Dialogo>
  );
}

/**
 * Da cosa è composta una cifra del prospetto.
 *
 * Davanti a qualche migliaio di euro la domanda è «da dove escono?». Senza le
 * righe l'unica risposta possibile è fidarsi, e un numero di cui non si può
 * verificare la provenienza non regge una decisione.
 */
function DialogoDettaglio({
  cella,
  finestra,
  formatoData,
  onChiudi,
}: {
  cella: CellaScelta;
  /** La stessa finestra con cui è stato chiesto il prospetto: vedi sopra. */
  finestra: string;
  formatoData: Parameters<typeof formattaData>[1];
  onChiudi: () => void;
}) {
  const chiaveRamo = cella.ramo.join(',');
  const query = `${finestra}&periodo=${encodeURIComponent(cella.periodo)}&categoriaId=${
    chiaveRamo || 'nessuna'
  }&verso=${cella.verso}`;
  const { dati, caricamento, errore, ricarica } = useCarico(
    () => api.get<DettaglioPrevisionale>(`/api/previsionale/dettaglio?${query}`),
    [cella.periodo, chiaveRamo, cella.verso, finestra],
  );

  const totale = dati
    ? dati.totali.movimentiCent + dati.totali.documentiCent + dati.totali.f24Cent + dati.totali.pianoCent
    : 0;

  // `scaduto` lo decide il motore confrontando la data di cassa attesa con
  // oggi: la stessa condizione con cui li ha trascinati qui, quindi le due
  // liste sommano sempre al totale dei documenti.
  const arretrati = (dati?.documenti ?? []).filter((d) => d.scaduto);
  const diCompetenza = (dati?.documenti ?? []).filter((d) => !d.scaduto);

  /**
   * Il reale e la stima, tenuti separati.
   *
   * «In corso» è il denaro già transitato in banca; «previsto» è ciò che il
   * periodo si aspetta di suo; «arretrato» ciò che si trascina da prima. Ogni
   * finestra mostra **le righe che compongono la sua cifra e nient'altro**:
   * altrimenti si aprirebbero tre numeri diversi per leggere sempre lo stesso
   * elenco, e nessuno dei tre reggerebbe un controllo.
   */
  // Il previsto del mese comprende quello che è già passato in banca: aprendolo
  // si devono vedere anche i movimenti, o l'elenco non somma alla cifra.
  // Il residuo è ciò che deve ancora muoversi: lì i movimenti già passati non
  // c'entrano. Il totale del mese invece li comprende.
  const mostraMovimenti = cella.parte === 'IN_CORSO' || cella.parte === 'PREVISTO' || cella.parte === 'TUTTO';
  const mostraArretrati = cella.parte === 'ARRETRATO' || cella.parte === 'TUTTO';
  const mostraCompetenza = cella.parte === 'PREVISTO' || cella.parte === 'RESIDUO' || cella.parte === 'TUTTO';
  // Gli scaduti sono un **sottoinsieme** dei documenti, non un addendo: per
  // avere la sola competenza vanno tolti, non sommati altrove.
  const previstoDelMese = dati
    ? dati.totali.movimentiCent +
      dati.totali.documentiCent -
      dati.totali.scadutiCent +
      dati.totali.f24Cent +
      dati.totali.pianoCent
    : 0;

  // I gruppi partono chiusi e mostrano il totale: cinque elenchi aperti sono
  // una parete, e la domanda che porta qui è quasi sempre «quanto» prima di
  // «quali». Con un gruppo solo però non c'è niente da scegliere, e far
  // premere per vedere l'unica cosa che c'è sarebbe un dispetto.
  const quantiGruppi = dati
    ? [
        mostraMovimenti && dati.movimenti.length > 0,
        mostraArretrati && arretrati.length > 0,
        mostraCompetenza && diCompetenza.length > 0,
        mostraCompetenza && dati.f24.length > 0,
        mostraCompetenza && dati.piano.length > 0,
      ].filter(Boolean).length
    : 0;
  const unGruppoSolo = quantiGruppi === 1;

  return (
    <Dialogo
      aperto
      // La colonna nel titolo: nel mese in corso le cifre apribili sono due, e
      // aperta la finestra non si ricorderebbe su quale si è premuto.
      titolo={`${cella.nomeCategoria} · ${cella.etichettaPeriodo}${
        cella.parte === 'IN_CORSO'
          ? ' · in corso'
          : cella.parte === 'PREVISTO'
            ? ' · previsto'
            : cella.parte === 'RESIDUO'
              ? ' · residuo'
              : cella.parte === 'ARRETRATO'
                ? ' · arretrato'
              : ''
      }`}
      onChiudi={onChiudi}
      larghezza="max-w-4xl"
    >
      {caricamento ? (
        <Caricamento />
      ) : errore || !dati ? (
        <Errore testo={errore ?? 'Caricamento non riuscito'} riprova={ricarica} />
      ) : (
        <div className="space-y-4">
          <div className="rounded-md bg-inchiostro-50 p-3 text-sm">
            {cella.parte === 'IN_CORSO' ? (
              <>
                <span className="text-inchiostro-600">Già passato in banca</span>{' '}
                <span className="num font-semibold">{formattaEuro(dati.totali.movimentiCent)}</span>
                <span className="mt-0.5 block text-xs text-inchiostro-600">
                  Solo denaro che si è davvero mosso. Il previsto per l’intero periodo è{' '}
                  <span className="num">{formattaEuro(totale)}</span>.
                </span>
              </>
            ) : cella.parte === 'PREVISTO' ? (
              <>
                <span className="text-inchiostro-600">Previsto per il periodo</span>{' '}
                <span className="num font-semibold">{formattaEuro(previstoDelMese)}</span>
                <span className="mt-0.5 block text-xs text-inchiostro-600">
                  Comprende i <span className="num">{formattaEuro(dati.totali.movimentiCent)}</span> già passati in
                  banca. Senza l’arretrato, che ha la sua colonna.
                </span>
              </>
            ) : cella.parte === 'RESIDUO' ? (
              <>
                <span className="text-inchiostro-600">Ancora da muoversi nel periodo</span>{' '}
                <span className="num font-semibold">
                  {formattaEuro(Math.max(previstoDelMese - dati.totali.movimentiCent, 0))}
                </span>
                <span className="mt-0.5 block text-xs text-inchiostro-600">
                  Il totale del periodo, <span className="num">{formattaEuro(previstoDelMese)}</span>, meno i{' '}
                  <span className="num">{formattaEuro(dati.totali.movimentiCent)}</span> già passati in banca. È questa
                  la cifra che costruisce il saldo di fine periodo.
                </span>
              </>
            ) : cella.parte === 'ARRETRATO' ? (
              <>
                <span className="text-inchiostro-600">Arretrato trascinato da prima</span>{' '}
                <span className="num font-semibold text-attenzione">{formattaEuro(dati.totali.scadutiCent)}</span>
                <span className="mt-0.5 block text-xs text-inchiostro-600">
                  Documenti con la data di cassa di un periodo precedente. Non entrano nel saldo a fine periodo: parte
                  potrebbe essere già rientrata e soltanto non riconciliata.
                </span>
              </>
            ) : (
              <>
                <span className="text-inchiostro-600">
                  {cella.verso === 'ENTRATA' ? 'Entrate' : 'Uscite'} del periodo
                </span>{' '}
                <span className="num font-semibold">{formattaEuro(totale)}</span>
              </>
            )}
            {cella.parte === 'TUTTO' && dati.totali.scadutiCent > 0 && (
              <span className="mt-0.5 block text-xs text-attenzione">
                di cui <span className="num">{formattaEuro(dati.totali.scadutiCent)}</span> di arretrato dai periodi
                precedenti
              </span>
            )}
          </div>

          {mostraMovimenti && dati.movimenti.length > 0 && (
            <SezioneDettaglio titolo="Movimenti in banca" aperta={unGruppoSolo} totaleCent={dati.totali.movimentiCent}>
              <table className="dati">
                <tbody>
                  {dati.movimenti.map((m, i) => (
                    // Un solo bonifico può comparire più volte, spezzato fra le
                    // fatture che sta pagando: la chiave include l'indice.
                    <tr key={`${m.id}-${i}`}>
                      <td className="num whitespace-nowrap">{formattaData(m.data, formatoData)}</td>
                      <td className="max-w-96 truncate" title={m.dicitura}>
                        {m.dicitura}
                      </td>
                      <td className="text-right">
                        <Importo cent={m.importoCent} tono="auto" />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </SezioneDettaglio>
          )}

          {/* Gli arretrati stanno in una sezione loro, non mescolati agli altri con
              un'etichetta accanto. Sono due cose che rispondono a due domande
              diverse: «cosa devo incassare questo mese» e «cosa mi porto dietro
              dai mesi scorsi». Sommate insieme, la seconda si nasconde dentro la
              prima proprio quando è quella che conta. */}
          {mostraArretrati && arretrati.length > 0 && (
            <SezioneDettaglio
              titolo="Arretrati dai periodi precedenti"
              nota="La data di cassa è già passata: si trascinano sul periodo in corso finché non rientrano."
              totaleCent={dati.totali.scadutiCent}
              tono="attenzione"
              aperta={unGruppoSolo}
            >
              <TabellaDocumenti righe={arretrati} formatoData={formatoData} conRitardo />
            </SezioneDettaglio>
          )}

          {mostraCompetenza && diCompetenza.length > 0 && (
            <SezioneDettaglio titolo="In scadenza nel periodo" aperta={unGruppoSolo} totaleCent={dati.totali.documentiCent - dati.totali.scadutiCent}>
              <TabellaDocumenti righe={diCompetenza} formatoData={formatoData} />
            </SezioneDettaglio>
          )}

          {mostraCompetenza && dati.f24.length > 0 && (
            <SezioneDettaglio titolo="F24 da versare" aperta={unGruppoSolo} totaleCent={dati.totali.f24Cent}>
              <table className="dati">
                <tbody>
                  {dati.f24.map((f) => (
                    <tr key={f.id}>
                      <td className="num whitespace-nowrap">{formattaData(f.dataScadenza, formatoData)}</td>
                      <td className="font-medium">
                        <Link to="/f24" className="underline decoration-dotted underline-offset-2">
                          {f.descrizione}
                        </Link>
                      </td>
                      <td className="text-right">
                        <Importo cent={f.saldoCent} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </SezioneDettaglio>
          )}

          {mostraCompetenza && dati.piano.length > 0 && (
            <SezioneDettaglio titolo="Budget e ricorrenti" aperta={unGruppoSolo} totaleCent={dati.totali.pianoCent}>
              <table className="dati">
                <tbody>
                  {dati.piano.map((v, i) => (
                    <tr key={`${v.etichetta}-${i}`}>
                      <td>
                        {v.etichetta}
                        {/* La stima di una controparte cede alle sue fatture vere:
                            dirlo qui, con i numeri, è la differenza fra un conto
                            che si capisce e uno che sembra sbagliato. */}
                        {v.copertoCent !== undefined && v.copertoCent > 0 && (
                          <span className="mt-0.5 block text-xs text-inchiostro-500">
                            stima <span className="num">{formattaEuro(v.importoCent)}</span> − fatture già arrivate nel
                            periodo <span className="num">{formattaEuro(v.copertoCent)}</span> (
                            {v.fattureCheCoprono?.join(', ')}) ={' '}
                            <span className="num font-medium">{formattaEuro(v.importoCent - v.copertoCent)}</span>. La
                            fattura prende il posto della stima, e può stare su un’altra voce.
                          </span>
                        )}
                      </td>
                      <td className="text-xs text-inchiostro-500">
                        {v.fonte === 'BUDGET' ? 'budget' : 'ricorrente'}
                      </td>
                      <td className="text-right">
                        {v.copertoCent !== undefined && v.copertoCent > 0 ? (
                          <Importo cent={v.importoCent - v.copertoCent} />
                        ) : (
                          <Importo cent={v.importoCent} />
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {/* Il totale della sezione non è la somma delle righe, e tacerlo
                  farebbe sospettare un errore di calcolo dove non c'è. */}
              <p className="mt-1 px-3 pb-2 text-xs text-inchiostro-500">
                Il piano pesa solo per quanto eccede ciò che è già successo e le fatture della sua voce, sottovoci
                comprese — o, per la stima di un fornitore o di un cliente, le fatture di quella controparte: senza,
                la stessa spesa verrebbe prevista due volte. Nel mese in corso pesano solo le scadenze fisse, non le
                stime.
              </p>
            </SezioneDettaglio>
          )}

          {(!mostraMovimenti || dati.movimenti.length === 0) &&
            (!mostraArretrati || arretrati.length === 0) &&
            (!mostraCompetenza ||
              (diCompetenza.length === 0 && dati.f24.length === 0 && dati.piano.length === 0)) && (
              <Vuoto
                titolo="Nessuna riga"
                testo={
                  cella.parte === 'IN_CORSO'
                    ? 'In questo periodo la voce non ha ancora movimenti in banca.'
                    : cella.parte === 'PREVISTO'
                      ? 'Su questa voce il periodo non si aspetta niente di suo: quello che c’è è tutto arretrato.'
                      : cella.parte === 'RESIDUO'
                        ? 'Su questa voce non resta niente da muoversi: quello che il periodo si aspettava è già passato in banca.'
                      : cella.parte === 'ARRETRATO'
                        ? 'Su questa voce non c’è arretrato: tutto quello che si aspetta è di questo periodo.'
                        : 'In questo periodo la voce non ha movimenti né documenti.'
                }
              />
            )}
        </div>
      )}
    </Dialogo>
  );
}

/**
 * L'elenco dei documenti di una sezione del dettaglio.
 *
 * Le due sezioni — arretrati e in scadenza — mostrano le stesse colonne, e
 * tenerle in un componente solo è ciò che garantisce che restino uguali: due
 * copie divergono alla prima colonna aggiunta a una delle due.
 */
function TabellaDocumenti({
  righe,
  formatoData,
  conRitardo,
}: {
  righe: DettaglioPrevisionale['documenti'];
  formatoData: Parameters<typeof formattaData>[1];
  /** Negli arretrati la domanda è «da quanto»: la colonna compare solo lì. */
  conRitardo?: boolean;
}) {
  return (
    <table className="dati">
      <thead>
        <tr>
          <th className="w-28">Scadenza</th>
          <th
            className="w-32"
            title="La scadenza corretta col ritardo con cui questa controparte paga di solito. È la data che colloca il documento nel prospetto."
          >
            Attesa in cassa
          </th>
          <th>Documento</th>
          <th>Controparte</th>
          {conRitardo && <th className="w-24 text-right">Da</th>}
          <th className="text-right">Residuo</th>
        </tr>
      </thead>
      <tbody>
        {righe.map((d, i) => (
          <tr key={`${d.id}-${i}`}>
            <td className="num whitespace-nowrap">{formattaData(d.dataScadenza, formatoData)}</td>
            <td className="num whitespace-nowrap">
              {formattaData(d.dataCassaAttesa, formatoData)}
              {d.dataCassaAttesa !== d.dataScadenza && (
                <span
                  className="ml-1 text-[10px] text-inchiostro-500"
                  title="Differenza fra la scadenza scritta sul documento e quella attesa, dal ritardo osservato della controparte."
                >
                  {scartoGiorni(d.dataScadenza, d.dataCassaAttesa)}
                </span>
              )}
            </td>
            <td className="font-medium">
              <Link to={`/fatture/${d.id}`} className="underline decoration-dotted underline-offset-2">
                {d.numero}
              </Link>
            </td>
            <td className="max-w-64 truncate" title={d.controparte ?? undefined}>
              {d.controparte ?? '—'}
            </td>
            {conRitardo && (
              <td className="num whitespace-nowrap text-right text-xs text-attenzione">
                {giorniDaAllora(d.dataCassaAttesa)}
              </td>
            )}
            <td className="text-right">
              <Importo cent={d.residuoCent} />
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/** Da quanti giorni una data è passata: «12 g», «3 mesi». */
function giorniDaAllora(data: string): string {
  const giorni = Math.floor((Date.parse(`${oggiIso()}T00:00:00Z`) - Date.parse(`${data}T00:00:00Z`)) / 86_400_000);
  if (giorni < 1) return '—';
  if (giorni < 45) return `${giorni} g`;
  return `${Math.round(giorni / 30)} mesi`;
}

/** La data di oggi, come la scrive il database. */
function oggiIso(): string {
  return new Date().toISOString().slice(0, 10);
}

/** «+7g» o «−3g»: di quanto la data attesa si scosta dalla scadenza scritta. */
function scartoGiorni(scadenza: string, attesa: string): string {
  const g = Math.round((Date.parse(attesa) - Date.parse(scadenza)) / 86400000);
  return `${g > 0 ? '+' : '\u2212'}${Math.abs(g)}g`;
}

function SezioneDettaglio({
  titolo,
  totaleCent,
  nota,
  tono,
  aperta,
  children,
}: {
  titolo: string;
  totaleCent: number;
  /** Una riga che spiega perché queste righe stanno qui, quando non è ovvio. */
  nota?: string;
  tono?: 'attenzione';
  /** Se parte già aperta. Con un gruppo solo non ha senso far premere. */
  aperta?: boolean;
  children: ReactNode;
}) {
  return (
    // `<details>` e non uno stato React: l'apertura di un gruppo non è un dato
    // dell'applicazione, e questo la fa funzionare da tastiera e con i lettori
    // di schermo senza scrivere una riga di accessibilità.
    <details
      open={aperta}
      className={`sezione-gruppo rounded-md border ${
        tono === 'attenzione' ? 'border-attenzione/40' : 'border-inchiostro-200'
      }`}
    >
      <summary
        className={`flex cursor-pointer items-baseline justify-between gap-3 px-3 py-2 ${
          tono === 'attenzione' ? 'bg-attenzione-chiaro' : 'hover:bg-inchiostro-50'
        }`}
      >
        <span className="flex items-baseline gap-1.5">
          {/* Lo stesso segno del prospetto: chi ha imparato ad aprire una voce
              là sa già come si apre un gruppo qui. */}
          <span className="w-3 text-inchiostro-400" aria-hidden="true">
            <span className="da-aprire">+</span>
            <span className="da-chiudere">−</span>
          </span>
          <span>
            <span className="text-sm font-medium">{titolo}</span>
            {nota && <span className="mt-0.5 block text-xs font-normal text-inchiostro-600">{nota}</span>}
          </span>
        </span>
        <span className="num whitespace-nowrap text-sm">{formattaEuro(totaleCent)}</span>
      </summary>
      <div
        className={`max-h-64 overflow-y-auto border-t ${
          tono === 'attenzione' ? 'border-attenzione/40' : 'border-inchiostro-200'
        }`}
      >
        {children}
      </div>
    </details>
  );
}

/**
 * Una voce e tutte le sue discendenti. La griglia somma i rami, quindi il
 * dettaglio deve chiedere lo stesso ramo: la sola voce padre darebbe un elenco
 * più corto della cifra che sta spiegando.
 */
function ramoDi(id: string, figliDi: Map<string | null, Categoria[]>): string[] {
  /**
   * «Altro» è il contrario di un ramo: sono i valori messi **direttamente**
   * sulla voce, quelli che restano fuori dalle sue figlie. Espanderlo darebbe
   * di nuovo il totale del ramo, cioè la riga sopra — e l'id sintetico, spedito
   * com'è, arrivava al database come se fosse un identificativo.
   */
  if (id.endsWith(SUFFISSO_ALTRO)) return [id.slice(0, -SUFFISSO_ALTRO.length)];
  const raccolti = [id];
  for (let i = 0; i < raccolti.length; i++) {
    for (const f of figliDi.get(raccolti[i]) ?? []) raccolti.push(f.id);
  }
  return raccolti;
}

function SezioneRighe({
  titolo,
  righe,
  periodi,
  espansi,
  setEspansi,
  figliDi,
  tono,
  onApriCella,
  modificabile,
  budgetDi,
  onModificaBudget,
}: {
  titolo: string;
  righe: RigaGriglia[];
  periodi: Previsione['periodi'];
  espansi: Set<string>;
  setEspansi: (s: Set<string>) => void;
  figliDi: Map<string | null, Categoria[]>;
  tono: 'incassi' | 'uscite';
  onApriCella: (c: CellaScelta) => void;
  /**
   * Se in questo prospetto il budget si può scrivere. Vale solo a mesi: il
   * budget è per mese, e a granularità settimanale non c'è una casella in cui
   * metterlo senza inventarsi una ripartizione.
   */
  modificabile: boolean;
  budgetDi: Map<string, number>;
  onModificaBudget: (v: {
    categoriaId: string;
    nomeCategoria: string;
    verso: 'ENTRATA' | 'USCITA';
    mese: string;
    etichettaMese: string;
    importoCent: number;
  }) => void;
}) {
  return (
    <>
      <tr>
        {/* Il titolo in una cella sua, agganciata a sinistra come la colonna
            delle voci; il resto della riga in una seconda cella. Con una cella
            sola larga quanto la tabella, `sticky` non aveva dove attaccarsi e
            «Entrate» scorreva via insieme ai numeri. */}
        <td className="sticky left-0 z-10 bg-inchiostro-50 text-xs font-semibold uppercase tracking-wide text-inchiostro-600">
          {titolo}
        </td>
        <td
          colSpan={periodi.reduce((n, p) => n + (p.collocazione === 'CORRENTE' ? COLONNE_MESE_IN_CORSO : 1), 0)}
          className="bg-inchiostro-50"
        />
      </tr>
      {righe.map((r) => {
        const haFigli = (figliDi.get(r.categoria.id) ?? []).length > 0;
        // La riga di una categoria che è stata aperta si stacca appena dal
        // fondo: senza, in mezzo alle sue figlie sembra una sorella e non si
        // capisce più che il suo importo le comprende già tutte.
        const aperta = haFigli && espansi.has(r.categoria.id);
        const fondo = aperta ? 'bg-inchiostro-50' : 'bg-white';
        return (
          <tr key={r.categoria.id} className={aperta ? 'bg-inchiostro-50' : undefined}>
            <td className={`sticky left-0 z-10 ${fondo}`}>
              <span style={{ paddingLeft: `${r.livello * 1.1}rem` }} className="flex items-center gap-1.5">
                {haFigli ? (
                  <button
                    onClick={() => {
                      const n = new Set(espansi);
                      n.has(r.categoria.id) ? n.delete(r.categoria.id) : n.add(r.categoria.id);
                      setEspansi(n);
                    }}
                    className="w-4 text-inchiostro-400 hover:text-inchiostro-800"
                    aria-label={espansi.has(r.categoria.id) ? 'Comprimi' : 'Espandi'}
                  >
                    {espansi.has(r.categoria.id) ? '−' : '+'}
                  </button>
                ) : (
                  <span className="w-4" />
                )}
                {/* Le figlie in corsivo: sono un dettaglio della riga sopra, non
                    un'altra voce dello stesso rango. Si legge la gerarchia
                    anche senza contare i rientri. */}
                <span className={r.livello === 0 ? 'font-medium' : 'italic'}>{r.categoria.nome}</span>
                {r.categoria.sistema && (
                  <Etichetta
                    testo="sistema"
                    titolo="Raccoglie movimenti e documenti non ancora categorizzati. Non si assegna a mano."
                  />
                )}
              </span>
            </td>
            {periodi.map((p) => {
              const v = r.perPeriodo.get(p.chiave);
              const totale = v ? totaleQuote(v) : 0;
              if (!v || totale === 0)
                return (
                  <Fragment key={p.chiave}>
                    {/* Una cella vuota è esattamente dove un budget serve: se la
                        matita comparisse solo sulle celle piene, la voce che
                        manca del tutto resterebbe l'unica impossibile da
                        prevedere. */}
                    <CellePeriodo
                      periodo={p}
                      effettivoCent={0}
                      previstoCent={0}
                      tono={tono}
                      onModifica={
                        modificabile && p.collocazione !== 'PASSATO' && !r.categoria.sistema
                          ? () => {
                              const id = r.categoria.id.replace(SUFFISSO_ALTRO, '');
                              onModificaBudget({
                                categoriaId: id,
                                nomeCategoria: r.nomeEsteso ?? r.categoria.nome,
                                verso: r.categoria.verso as 'ENTRATA' | 'USCITA',
                                mese: p.chiave,
                                etichettaMese: p.etichetta,
                                importoCent: budgetDi.get(`${id}|${p.chiave}`) ?? 0,
                              });
                            }
                          : undefined
                      }
                    />
                  </Fragment>
                );
              const fonti = [
                v.movimenti > 0 ? `effettivo ${formattaEuro(v.movimenti)}` : null,
                v.documenti !== 0 ? `documenti ${formattaEuro(v.documenti)}` : null,
                v.scaduti !== 0 ? `arretrati ${formattaEuro(v.scaduti)}` : null,
                v.f24 !== 0 ? `F24 ${formattaEuro(v.f24)}` : null,
                v.piano > 0 ? `piano ${formattaEuro(v.piano)}` : null,
              ].filter(Boolean);
              return (
                <Fragment key={p.chiave}>
                  <CellePeriodo
                    periodo={p}
                    effettivoCent={v.movimenti}
                    previstoCent={totale}
                    arretratoCent={v.scaduti}
                    tono={tono}
                    onModifica={
                      // Non sul passato — riscriverebbe la storia, e la rotta
                      // lo rifiuta comunque — e non sulle voci di sistema, che
                      // raccolgono ciò che nessuno ha deciso.
                      modificabile && p.collocazione !== 'PASSATO' && !r.categoria.sistema
                        ? () => {
                            const id = r.categoria.id.replace(SUFFISSO_ALTRO, '');
                            onModificaBudget({
                              categoriaId: id,
                              nomeCategoria: r.nomeEsteso ?? r.categoria.nome,
                              verso: r.categoria.verso as 'ENTRATA' | 'USCITA',
                              mese: p.chiave,
                              etichettaMese: p.etichetta,
                              importoCent: budgetDi.get(`${id}|${p.chiave}`) ?? 0,
                            });
                          }
                        : undefined
                    }
                    onApri={(parte) =>
                      onApriCella({
                        parte,
                        periodo: p.chiave,
                        etichettaPeriodo: p.etichetta,
                        ramo: ramoDi(r.categoria.id, figliDi),
                        nomeCategoria: r.nomeEsteso ?? r.categoria.nome,
                        verso: r.categoria.verso as 'ENTRATA' | 'USCITA',
                      })
                    }
                  />
                </Fragment>
              );
            })}
          </tr>
        );
      })}
    </>
  );
}

/**
 * Quante colonne occupa il periodo in corso: in corso, totale mese, residuo,
 * arretrato, già fatto.
 *
 * Sta in un posto solo perché stava in cinque, e aggiungendo «residuo» uno si è
 * perso: le righe dei saldi avevano una colonna in meno e ogni mese scivolava
 * sotto quello prima — il saldo di fine ottobre compariva sotto settembre.
 * Chi aggiunge una colonna a `CellePeriodo` cambia questo numero, e il resto
 * della tabella segue.
 */
const COLONNE_MESE_IN_CORSO = 5;

/**
 * Una cifra che vale per il periodo intero: la cassa con cui apre, il saldo con
 * cui chiude.
 *
 * Nel periodo in corso le colonne sono quattro, e la cifra sta **sotto la
 * prima**, quella del già passato in banca. Prima occupava le quattro insieme e,
 * allineata a destra, finiva incolonnata sotto «già fatto»: il saldo del mese si
 * leggeva a due colonne di distanza dal mese, appiccicato a quello dopo.
 */
function CellaDelPeriodo({
  periodo,
  children,
}: {
  periodo: Previsione['periodi'][number];
  children: ReactNode;
}) {
  if (periodo.collocazione !== 'CORRENTE') return <td className="text-right">{children}</td>;
  return (
    <>
      <td className="whitespace-nowrap text-right">{children}</td>
      {/* Una cella sola che copre il resto, non una per colonna: con le celle
          vuote contate a mano, all'arrivo di «residuo» qui ne mancava una, e
          ottobre finiva sotto settembre. */}
      <td colSpan={COLONNE_MESE_IN_CORSO - 1} />
    </>
  );
}

/**
 * Le celle di un periodo. Il periodo **in corso** ne occupa quattro — già
 * passato in banca, ancora da pagare o incassare nel mese, arretrato, e quanto
 * manca — perché è lì che servono numeri distinti: a metà mese la domanda è
 * «quanto è già successo, quanto deve ancora succedere, e quanto è roba vecchia
 * che mi trascino?».
 *
 * Il 17 set 2026 c'è stata per poche ore una quinta colonna, «budget». È stata
 * tolta: una stima nel mese in corso non si somma ai fatti — prevedere 18 mila
 * e vederne entrare 23 non fa 41 — e ora il motore non la conta più (vedi
 * `pesaNelPeriodo`). Il budget serve ai mesi futuri.
 *
 * L'arretrato è arrivato per ultimo e per un motivo preciso: senza, un mese
 * con 200 mila di incassi previsti sembrava un mese da 200 mila di lavoro,
 * mentre 171 mila erano crediti vecchi. La cifra era giusta e la lettura no.
 *
 * Negli altri periodi la colonna è una sola: nel passato esiste solo
 * l'effettivo, nel futuro solo la previsione.
 */
function CellePeriodo({
  periodo,
  effettivoCent,
  previstoCent,
  arretratoCent = 0,
  tono,
  grassetto,
  onApri,
  onModifica,
}: {
  periodo: Previsione['periodi'][number];
  effettivoCent: number;
  previstoCent: number;
  /**
   * Quanta parte del previsto è trascinata dai periodi precedenti.
   *
   * È un **di cui**, non un addendo: sta dentro `previstoCent`. Tenerlo fuori
   * avrebbe voluto dire che il saldo a fine periodo e i totali non tornavano
   * più con le colonne che si vedono, e un prospetto che non quadra con sé
   * stesso non lo si legge due volte.
   */
  arretratoCent?: number;
  tono: 'incassi' | 'uscite' | 'auto' | 'saldo';
  grassetto?: boolean;
  /** Quando c'è, la cifra apre il dettaglio di ciò che la compone. */
  onApri?: (parte: CellaScelta['parte']) => void;
  /**
   * Quando c'è, la cella si può scrivere: compare una matita al passaggio del
   * mouse. È un gesto **a sé** e non un clic sul numero perché il numero è una
   * somma, e ciò che si modifica ne è solo una parte.
   */
  onModifica?: () => void;
}) {
  const vuoto = <span className="text-inchiostro-300">—</span>;

  const matita = onModifica ? (
    <button
      onClick={onModifica}
      // Invisibile finché non serve: una matita su ogni cella di ogni riga
      // futura sarebbe un prospetto pieno di icone invece che di numeri.
      className="ml-1 align-middle text-inchiostro-300 opacity-0 transition-opacity hover:text-skin focus-visible:opacity-100 group-hover/cella:opacity-100"
      title="Scrivi il budget di questa voce per questo periodo"
      aria-label="Modifica il budget"
    >
      <svg viewBox="0 0 16 16" className="inline h-3 w-3" fill="currentColor" aria-hidden="true">
        <path d="M11.5 1.5a1.7 1.7 0 0 1 2.4 2.4l-.8.8-2.4-2.4.8-.8ZM9.6 3.4l2.4 2.4-6.6 6.6-3 .6.6-3 6.6-6.6Z" />
      </svg>
    </button>
  ) : null;

  /**
   * La cifra apribile resta una cifra. Il tratteggio sotto basta a dire che si
   * può premere: un prospetto numerico dove ogni numero è un collegamento
   * colorato smette di leggersi come una tabella.
   */
  const apribile = (contenuto: ReactNode, parte: CellaScelta['parte']) =>
    onApri ? (
      <button
        onClick={() => onApri(parte)}
        className="underline decoration-dotted decoration-inchiostro-300 underline-offset-4 hover:decoration-inchiostro-800"
        title="Guarda da cosa è composta"
      >
        {contenuto}
      </button>
    ) : (
      contenuto
    );

  if (periodo.collocazione !== 'CORRENTE') {
    const valore = previstoCent || effettivoCent;
    return (
      <td className="group/cella whitespace-nowrap text-right">
        {valore === 0 ? (onModifica ? vuoto : vuoto) : apribile(<Importo cent={valore} tono={tono} grassetto={grassetto} />, 'TUTTO')}
        {matita}
      </td>
    );
  }

  const niente = previstoCent === 0 && effettivoCent === 0;
  /**
   * Il previsto del mese **intero**, già passato compreso: è la cifra che il
   * mese si aspetta in tutto.
   *
   * Per qualche ora è stato il solo residuo — quello che deve ancora muoversi —
   * e non si capiva più cosa ci fosse dentro. Trezy, sul mese in corso, scrive
   * nella stessa cella «già successo / previsto del mese» (`0 / 180K`): il
   * previsto è il totale, e accanto si legge quanto ne è già passato. La stessa
   * scelta qui: «in corso» a sinistra, il totale del mese qui, e quanto ne è
   * fatto in percentuale in fondo. L'arretrato resta fuori, nella sua colonna.
   */
  const previstoDelMeseCent = previstoCent - arretratoCent;
  /**
   * Quanto deve ancora muoversi. **Mai negativo**: se si è già incassato più di
   * quanto il mese prevedeva, non resta niente da incassare — quel di più è già
   * dentro la cassa di oggi, e scriverlo come «−2.000 da incassare» direbbe una
   * cosa che non esiste.
   */
  const residuoCent = Math.max(previstoDelMeseCent - effettivoCent, 0);
  const cella = 'bg-inchiostro-50 text-right';

  return (
    <>
      <td className={cella}>
        {niente ? vuoto : apribile(<Importo cent={effettivoCent} tono={tono} grassetto={grassetto} />, 'IN_CORSO')}
      </td>
      {/* Il previsto del mese intero, arretrato escluso. Il già passato è qui
          dentro: la colonna «in corso» ne è la parte già avvenuta, non un
          addendo. */}
      <td
        className={`${cella} group/cella whitespace-nowrap text-inchiostro-500`}
        title={
          niente
            ? undefined
            : `Quello che il mese si aspetta in tutto: ${formattaEuro(effettivoCent)} già passati in banca e ` +
              `${formattaEuro(previstoDelMeseCent - effettivoCent)} ancora da muoversi. L'arretrato è a parte.`
        }
      >
        {previstoDelMeseCent === 0 ? vuoto : apribile(formattaEuro(previstoDelMeseCent), 'PREVISTO')}
        {matita}
      </td>
      {/* Il residuo accanto al totale: è la cifra che entra nel saldo di fine
          periodo, ed è quella che si va a cercare quando si chiede «quanto
          manca ancora da pagare». */}
      <td
        className={`${cella} whitespace-nowrap`}
        title={
          niente
            ? undefined
            : `Da muoversi ancora entro fine periodo: ${formattaEuro(previstoDelMeseCent)} previsti meno ` +
              `${formattaEuro(effettivoCent)} già passati in banca. È questa la cifra che costruisce il saldo di fine periodo.`
        }
      >
        {residuoCent === 0 ? vuoto : apribile(formattaEuro(residuoCent), 'RESIDUO')}
      </td>
      {/* L'arretrato accanto al previsto, non dentro un suggerimento: era
          proprio il non vederlo a far sembrare che i 200 mila di settembre
          fossero tutti lavoro del mese. E si apre come le altre cifre, perché
          «quali fatture sono» è la domanda immediatamente successiva. */}
      <td
        className={`${cella} whitespace-nowrap`}
        title={
          arretratoCent === 0
            ? undefined
            : `Documenti con la data di cassa di un periodo precedente. Non entrano nel saldo a fine periodo: l'effetto si legge nella riga sotto il saldo.`
        }
      >
        {arretratoCent === 0 ? (
          vuoto
        ) : (
          <span className="text-xs text-attenzione">{apribile(formattaEuro(arretratoCent), 'ARRETRATO')}</span>
        )}
      </td>
      <td
        className={cella}
        title={
          niente
            ? undefined
            : `${quotaFatta(previstoDelMeseCent, effettivoCent)} del previsto del mese è già passato in banca: ` +
              `${formattaEuro(effettivoCent)} su ${formattaEuro(previstoDelMeseCent)}, ne mancano ` +
              `${formattaEuro(previstoDelMeseCent - effettivoCent)}` +
              (arretratoCent === 0 ? '.' : `, oltre a ${formattaEuro(arretratoCent)} di arretrato.`)
        }
      >
        {niente ? (
          vuoto
        ) : (
          // In percentuale invece che in euro: la domanda a mese aperto è «a
          // che punto siamo», e una quota si confronta a colpo d'occhio fra
          // righe di ordini di grandezza diversi, che gli euro non permettono.
          // La cifra in euro resta, nel suggerimento.
          <span className="text-xs text-inchiostro-600">{quotaFatta(previstoDelMeseCent, effettivoCent)}</span>
        )}
      </td>
    </>
  );
}

/**
 * Quanto del previsto del mese è **già** passato in banca.
 *
 * Era «quanto manca», e a metà mese è la stessa informazione girata al
 * contrario — ma la si legge peggio: 95% di mancante su una riga e 12% su
 * un'altra non dicono a colpo d'occhio a che punto è il mese. Trezy mostra
 * l'avanzamento, ed è quello che si guarda davvero.
 *
 * Con previsto a zero la percentuale non esiste — non si divide per zero, e
 * «infinito per cento» non dice niente. Lì si ripiega sugli euro: è successo
 * qualcosa che non era previsto, ed è proprio il caso che si vuole notare.
 */
function quotaFatta(previstoCent: number, effettivoCent: number): string {
  if (previstoCent === 0) return effettivoCent === 0 ? '—' : formattaEuro(effettivoCent);
  const quota = (effettivoCent / Math.abs(previstoCent)) * 100;
  if (quota !== 0 && Math.abs(quota) < 0.5) return '<1%';
  return `${Math.round(quota)}%`;
}

function Totale({
  etichetta,
  periodi,
  valore,
  effettivo,
  arretrato,
  tono,
}: {
  etichetta: string;
  periodi: Previsione['periodi'];
  valore: (p: Previsione['periodi'][number]) => number;
  /** Quanto di quel totale è già transitato in banca, per il periodo in corso. */
  effettivo?: (p: Previsione['periodi'][number]) => number;
  /** E quanto ne è trascinato dai periodi precedenti. */
  arretrato?: (p: Previsione['periodi'][number]) => number;
  tono: 'incassi' | 'uscite';
}) {
  return (
    <tr className="bg-inchiostro-50/60">
      <td className="sticky left-0 z-10 bg-inchiostro-50 font-medium">{etichetta}</td>
      {periodi.map((p) => (
        <Fragment key={p.chiave}>
          <CellePeriodo
            periodo={p}
            effettivoCent={effettivo ? effettivo(p) : 0}
            previstoCent={valore(p)}
            arretratoCent={arretrato ? arretrato(p) : 0}
            tono={tono}
            grassetto
          />
        </Fragment>
      ))}
    </tr>
  );
}

/**
 * Rateizzazione di una liquidazione. Le rate sono **previsioni di cassa**, non
 * un piano concordato con l'erario: Mastro non sa se la dilazione è stata
 * accettata, sa solo distribuire l'uscita su più mesi come gli si dice.
 */
